import React, { useState, useEffect, useLayoutEffect, useRef, useCallback, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useIntl } from "react-intl";
import { SERVER_URL } from "../api";
import { getQuizById } from "../api/quiz";
import { postAnswer } from "../api/answer";
import { getGameById } from "../api/game";
import { updateGroup } from "../api/group";
import { setGame, setGroup, updateTime } from "../redux/actions";
import {
	EscapeRoomElement,
	getPlayablePages,
	getElementsForViewport,
	getErViewportDimensions,
	pickErViewportFromSize,
} from "../components/EscapeRoomRenderer";
import { getButtonIconComponent } from "../components/escape-room/buttonIconMap";
import QuizFeedbackModal from "../components/escape-room/QuizFeedbackModal";
import { resolveQuizFeedbackThemeForElement } from "../components/escape-room/quizFeedbackThemes";
import { ROUTES, GAME_TIME_SECS } from "../utils/constants";
import { isRegistrationPage } from "../utils/escapeRoomConstants";
import { prefetchEscapeRoomPage } from "../utils/escapeRoomPagePrefetch";
import { patchScoreDelta } from "../api/group";
import { BUTTON_HIDE_EXIT_MS } from "../components/escape-room/ButtonHideCountdownFrame";
import { postKpiEvent } from "../api/kpi";
import { bumpSessionKpi } from "../utils/sessionKpiStore";
import { useTimer } from "../hooks/useTimer";
import {
	updateStats,
	getTimer,
	clearItem,
	updateTimer,
	setLastTickNow,
	getLastTick,
	setLastTick,
	formatMinutes,
	formatSeconds,
} from "../utils/helpers";
import { alertError } from "../utils/logger";
import { groupLinkedGameId, resetPlaySessionToEnterCode } from "../utils/resetPlaySession";
import { saveErPlaySession, loadErPlaySession, clearErPlaySession } from "../utils/erPlaySessionStorage";
import { getGroupById } from "../api/group";
import { collectFontStylesheetUrls, injectEscapeRoomFontLinks } from "../utils/escapeRoomFonts";
import { ensureTextEffectStylesInjected, resolveEscapeRoomMediaUrl } from "../components/escape-room/erRendererStyleUtils";
import { escapeRoomEditorBackgroundSurfaceStyle, EscapeRoomBackgroundMediaLayer, isEscapeRoomVideoUrl } from "../components/escape-room/escapeRoomBackgroundMedia";
import EscapeRoomPageGlitchOverlay, { getPageTransitionWrapperStyle } from "../components/escape-room/EscapeRoomPageGlitchOverlay";
import EscapeRoomPageMistOverlay from "../components/escape-room/EscapeRoomPageMistOverlay";
import EscapeRoomPageBlueprintOverlay from "../components/escape-room/EscapeRoomPageBlueprintOverlay";
import {
	buildComicStaggerIndexMap,
	getComicPageBackgroundStyle,
	shouldApplyComicPageElementTransition,
} from "../components/escape-room/erComicPageTransition";
import {
	buildScatterStaggerIndexMap,
	getScatterPageBackgroundStyle,
	shouldApplyScatterPageElementTransition,
} from "../components/escape-room/erScatterPageTransition";
import {
	buildRevealStaggerIndexMap,
	getRevealPageBackgroundStyle,
	shouldApplyRevealPageElementTransition,
} from "../components/escape-room/erRevealPageTransition";
import {
	isStaggerEffectTransition,
	buildStaggerEffectIndexMap,
	getStaggerEffectPageBackgroundStyle,
	shouldApplyStaggerEffectPageElementTransition,
} from "../components/escape-room/erStaggerEffectTransitions";
import EscapeRoomPageFlashOverlay from "../components/escape-room/EscapeRoomPageFlashOverlay";
import { useMedia } from "../contexts/MediaContext";
import { computePlayPageTransition, isFlashEnterTransition } from "../components/escape-room/escapeRoomPageTransitionUtils";
import { parseCssColor } from "../utils/gameColors";
import { getElementEntryAnimationStyle } from "../components/escape-room/erEntryAnimation";
import { quizTextAnswersMatch, resolveCustomQuizDone, accumulateCustomQuizPoints } from "../components/escape-room/MatchFillQuizPlay";

function normScId(v) {
	if (v == null || v === "") return "";
	return String(v).trim();
}

function resolveGroupScoreFromApi(serverGroup, localGroup, delta = 0) {
	const serverScore = Number(serverGroup?.score);
	if (Number.isFinite(serverScore)) return serverScore;
	const local = Number(localGroup?.score);
	const base = Number.isFinite(local) ? local : 0;
	return base + (Number.isFinite(delta) ? delta : 0);
}

const RETIRED_PAGE_TRANSITION_IDS = new Set([
	"brushStroke",
	"brushStrokeReverse",
	"mosaic",
	"iris",
	"shutter",
	"pageTurn",
]);

function normalizePageTransitionForPlay(id) {
	const v = id || "none";
	return RETIRED_PAGE_TRANSITION_IDS.has(v) ? "fade" : v;
}

/** Clave estable para timers de botones "tras X s" sin depender del array `elements` (nueva referencia cada render). */
function afterSecondsButtonsDependencyKey(els) {
	return (els || [])
		.filter((e) => e?.type === "button" && String(e?.content?.showWhen || "") === "afterSeconds")
		.map((e) => `${String(e.elementId)}:${Number(e.content?.showAfterSeconds) || 0}`)
		.sort()
		.join("|");
}

function hideAfterButtonsDependencyKey(els) {
	return (els || [])
		.filter((e) => e?.type === "button" && Math.max(0, Number(e?.content?.hideAfterSeconds) || 0) > 0)
		.map((e) => `${String(e.elementId)}:${Number(e.content?.hideAfterSeconds) || 0}`)
		.sort()
		.join("|");
}

const ER_MODAL_CLOSE_BTN_STYLE = {
	position: "absolute",
	top: 8,
	right: 8,
	zIndex: 10,
	width: 36,
	height: 36,
	padding: 0,
	margin: 0,
	border: "none",
	borderRadius: "50%",
	background: "rgba(255,255,255,0.95)",
	color: "#334155",
	cursor: "pointer",
	display: "flex",
	alignItems: "center",
	justifyContent: "center",
	boxShadow: "0 1px 4px rgba(0,0,0,0.18)",
	WebkitAppearance: "none",
	appearance: "none",
	lineHeight: 0,
};

function EscapeRoomModalCloseButton({ onClick, title }) {
	return (
		<button type="button" onClick={onClick} style={ER_MODAL_CLOSE_BTN_STYLE} title={title} aria-label={title}>
			<svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
				<path
					fill="currentColor"
					d="M19 6.41 17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"
				/>
			</svg>
		</button>
	);
}

const PLAY_GRAPH_VIEWPORTS = ["desktop", "tablet", "mobile"];

function resolveQuizId(rawQuizId) {
	if (!rawQuizId) return null;
	if (typeof rawQuizId === "string") {
		const t = rawQuizId.trim();
		return t && t !== "[object Object]" ? t : null;
	}
	if (typeof rawQuizId === "object") {
		if (rawQuizId._id) return String(rawQuizId._id);
		if (rawQuizId.id) return String(rawQuizId.id);
	}
	return null;
}

/** Incluye quizzes anidados en modales de botón/pista. */
function collectQuizIdsFromElements(elements, into = new Set()) {
	for (const el of elements || []) {
		if (el?.type === "quiz") {
			const id = resolveQuizId(el.content?.quizId);
			if (id) into.add(id);
		}
		const c = el?.content;
		if (!c) continue;
		if (el.type === "button" && Array.isArray(c.modalDesign?.elements)) {
			collectQuizIdsFromElements(c.modalDesign.elements, into);
		}
		if (el.type === "hint") {
			if (Array.isArray(c.hintModalDesign?.elements)) {
				collectQuizIdsFromElements(c.hintModalDesign.elements, into);
			}
			if (Array.isArray(c.confirmModalDesign?.elements)) {
				collectQuizIdsFromElements(c.confirmModalDesign.elements, into);
			}
		}
	}
	return into;
}

function webFindPageIndex(pages, tid) {
	return pages.findIndex((p) => String(p.pageId || p._id) === String(tid));
}

function entryPageIndexForScenario(pages, scenarioId) {
	const sid = normScId(scenarioId);
	if (!sid) return -1;
	let best = -1;
	let bestOrder = Infinity;
	for (let i = 0; i < pages.length; i++) {
		if (normScId(pages[i]?.scenarioId) !== sid) continue;
		const o = Number(pages[i]?.order) || i;
		if (o < bestOrder) {
			bestOrder = o;
			best = i;
		}
	}
	return best;
}

function walkOutgoingPageIndices(elements, pages, pageIdx, findIdx, out) {
	if (!elements?.length) return;
	for (const e of elements) {
		if (e.type === "button") {
			const a = e.content?.action;
			if (a === "nextPage") {
				out.add(Math.min(pages.length - 1, pageIdx + 1));
			} else if (a === "goToPage" && e.content?.targetPageId) {
				const ix = findIdx(e.content.targetPageId);
				if (ix >= 0) out.add(ix);
			} else if (a === "openModal" && e.content?.modalDesign?.elements?.length) {
				walkOutgoingPageIndices(e.content.modalDesign.elements, pages, pageIdx, findIdx, out);
			}
		} else if (e.type === "hint") {
			const c = e.content || {};
			for (const key of ["hintModalDesign", "confirmModalDesign"]) {
				const des = c[key];
				if (des?.elements?.length) walkOutgoingPageIndices(des.elements, pages, pageIdx, findIdx, out);
			}
		}
	}
}

function collectOutgoingPageIndicesFromPage(pages, pageIdx) {
	const findIdx = (tid) => webFindPageIndex(pages, tid);
	const out = new Set();
	for (const vp of PLAY_GRAPH_VIEWPORTS) {
		const els = getElementsForViewport(pages[pageIdx], vp) || [];
		walkOutgoingPageIndices(els, pages, pageIdx, findIdx, out);
	}
	return out;
}

function ufFind(parent, x) {
	const k = normScId(x);
	if (!k) return "";
	if (!parent.has(k)) parent.set(k, k);
	if (parent.get(k) === k) return k;
	const r = ufFind(parent, parent.get(k));
	parent.set(k, r);
	return r;
}
function ufUnion(parent, a, b) {
	const ra = ufFind(parent, normScId(a));
	const rb = ufFind(parent, normScId(b));
	if (ra && rb && ra !== rb) parent.set(ra, rb);
}

function computeExclusiveBranchGroups(pages) {
	const parent = new Map();
	const getOut = (pi) => collectOutgoingPageIndicesFromPage(pages, pi);

	for (let i = 0; i < pages.length; i++) {
		const cur = normScId(pages[i]?.scenarioId);
		const scenFromPage = new Set();
		for (const t of getOut(i)) {
			const sc = normScId(pages[t]?.scenarioId);
			if (sc && sc !== cur) scenFromPage.add(sc);
		}
		if (scenFromPage.size >= 2) {
			const ids = [...scenFromPage];
			for (let j = 1; j < ids.length; j++) ufUnion(parent, ids[0], ids[j]);
		}
	}

	const scenariosSeen = new Set();
	for (let p = 0; p < pages.length; p++) {
		const s = normScId(pages[p]?.scenarioId);
		if (!s || scenariosSeen.has(s)) continue;
		scenariosSeen.add(s);
		const childScenarios = new Set();
		for (let i = 0; i < pages.length; i++) {
			if (normScId(pages[i]?.scenarioId) !== s) continue;
			for (const t of getOut(i)) {
				const ts = normScId(pages[t]?.scenarioId);
				if (!ts || ts === s) continue;
				const ent = entryPageIndexForScenario(pages, ts);
				if (ent >= 0 && t === ent) childScenarios.add(ts);
			}
		}
		if (childScenarios.size >= 2) {
			const ids = [...childScenarios];
			for (let j = 1; j < ids.length; j++) ufUnion(parent, ids[0], ids[j]);
		}
	}

	const allScenarioIds = new Set();
	pages.forEach((pg) => {
		const x = normScId(pg?.scenarioId);
		if (x) allScenarioIds.add(x);
	});
	const byRoot = new Map();
	allScenarioIds.forEach((id) => {
		const r = ufFind(parent, id);
		if (!r) return;
		if (!byRoot.has(r)) byRoot.set(r, new Set());
		byRoot.get(r).add(id);
	});
	return Array.from(byRoot.values()).filter((g) => g.size >= 2);
}

function suppressBranchAlternatesWeb(landedScenarioId, pages, setSuppressed) {
	const landed = normScId(landedScenarioId);
	if (!landed) return;
	const groups = computeExclusiveBranchGroups(pages);
	const g = groups.find((gr) => gr.has(landed));
	if (!g || g.size < 2) return;
	setSuppressed((prev) => {
		const n = new Set();
		prev.forEach((x) => n.add(normScId(x)));
		g.forEach((id) => {
			const x = normScId(id);
			if (x && x !== landed) n.add(x);
		});
		return n;
	});
}

function scenariosVisitedUpToPage(pages, pageIdx) {
	const seen = new Set();
	for (let i = 0; i <= pageIdx; i++) {
		const s = normScId(pages[i]?.scenarioId);
		if (s) seen.add(s);
	}
	return seen;
}

function AmbientMusicPlayer({ url, loop, volume }) {
	const audioRef = useRef(null);
	const { isMediaUnlocked } = useMedia();
	const resolved = url ? resolveEscapeRoomMediaUrl(url) : "";
	const volRaw = Number(volume);
	const vol = Number.isFinite(volRaw) ? Math.min(1, Math.max(0, volRaw)) : 0.5;
	const shouldLoop = loop !== false;

	useEffect(() => {
		const el = audioRef.current;
		if (!el || !resolved) return undefined;
		const prev = el.getAttribute("data-src") || "";
		if (prev !== resolved) {
			el.src = resolved;
			el.setAttribute("data-src", resolved);
		}
		el.loop = shouldLoop;
		el.volume = vol;
		if (isMediaUnlocked) {
			try {
				const p = el.play();
				if (p && typeof p.catch === "function") p.catch(() => {});
			} catch (_) {}
		}
		return undefined;
	}, [resolved, shouldLoop, vol, isMediaUnlocked]);

	if (!resolved) return null;
	return <audio ref={audioRef} preload="auto" data-er-autoplay="1" style={{ display: "none" }} />;
}

const MODAL_SIZES = { small: { width: 400, height: 300 }, medium: { width: 640, height: 480 }, large: { width: 800, height: 600 } };

export default function EscapeRoomPlay() {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const intl = useIntl();
	const t = useCallback((id, values) => intl.formatMessage({ id }, values), [intl]);
	const game = useSelector((state) => state.game);
	const group = useSelector((state) => state.group);
	const groupGameIdStr = useMemo(() => groupLinkedGameId(group), [group]);

	const escapeRoom = game?.escapeRoom;
	const showPlayerScore = escapeRoom?.showPlayerScore !== false;
	/** Misma referencia mientras `escapeRoom.pages` no cambie; evita bucles infinitos en useEffect que dependían de un array nuevo cada render ([...pages]). */
	const rawPages = escapeRoom?.pages;
	const playablePages = useMemo(
		() => getPlayablePages(Array.isArray(rawPages) ? rawPages : []),
		[rawPages]
	);

	const [pageIndex, setPageIndex] = useState(() => group?.escapeRoomPageIndex ?? 0);
	const [answers, setAnswers] = useState(() => group?.escapeRoomAnswers || {});
	const [transitionType, setTransitionType] = useState("none");
	const [transitionDuration, setTransitionDuration] = useState(350);
	const [transitionKey, setTransitionKey] = useState(0);
	const [exitFlash, setExitFlash] = useState(null);
	const pageNavTimerRef = useRef(null);
	const [modalContent, setModalContent] = useState(null);
	const [quizFeedbackModal, setQuizFeedbackModal] = useState(null);
	/** Modal de pista con diseño (confirmModalDesign / hintModalDesign) */
	const [hintDesignModal, setHintDesignModal] = useState(null);
	/** Botones ya pulsados por página (para onlyOnce y showWhen allButtonsActivated) */
	const [activatedButtonsByPage, setActivatedButtonsByPage] = useState(() => ({}));
	const modalWasOpenRef = useRef(false);
	const [modalEntryAnimKey, setModalEntryAnimKey] = useState(0);
	const [footerOpen, setFooterOpen] = useState(false);
	/** Tras una bifurcación, el escenario no elegido deja de mostrarse en la barra */
	const [suppressedScenarioIds, setSuppressedScenarioIds] = useState(() => new Set());

	// Sincronizar una vez al montar si el grupo ya tiene progreso
	const syncedRef = useRef(false);
	const sessionRestoreRef = useRef(false);
	/** Páginas a las que ya se les aplicaron puntos por aterrizar en esta sesión (evitar doble suma) */
	const appliedPointsForPageRef = useRef(new Set());
	/** Aplicar puntos de la página actual una sola vez al cargar (p. ej. página 0 al entrar) */
	const appliedInitialLandingRef = useRef(false);
	useEffect(() => {
		if (syncedRef.current || !group) return;
		syncedRef.current = true;
		if (Number.isFinite(group.escapeRoomPageIndex)) setPageIndex(group.escapeRoomPageIndex);
		if (group.escapeRoomAnswers && Object.keys(group.escapeRoomAnswers).length > 0) setAnswers(group.escapeRoomAnswers);
	}, [group]);

	useEffect(() => {
		if (sessionRestoreRef.current) return;
		sessionRestoreRef.current = true;
		const session = loadErPlaySession();
		if (!session?.groupId || !session?.gameId) return;
		if (group?.finished) {
			clearErPlaySession();
			return;
		}
		// Código/partida nuevos en Redux: no restaurar la sesión anterior (abriría el ER equivocado)
		if (game?._id && String(game._id) !== String(session.gameId)) {
			clearErPlaySession();
			return;
		}
		(async () => {
			try {
				if (!game?._id || String(game._id) !== String(session.gameId)) {
					const gRes = await getGameById(session.gameId);
					if (gRes.status !== 200 || !gRes.data?.active) {
						clearErPlaySession();
						return;
					}
					dispatch(setGame(gRes.data));
				}
				if (group?._id && String(group._id) === String(session.groupId)) {
					if (Number.isFinite(session.pageIndex)) setPageIndex(session.pageIndex);
					if (session.answers && typeof session.answers === "object") setAnswers(session.answers);
					return;
				}
				const grRes = await getGroupById(session.groupId);
				if (grRes.status !== 200 || !grRes.data) {
					clearErPlaySession();
					return;
				}
				const restoredGroup = grRes.data;
				if (restoredGroup.finished || String(restoredGroup.game) !== String(session.gameId)) {
					clearErPlaySession();
					return;
				}
				const pageIdx = Number.isFinite(session.pageIndex) ? session.pageIndex : (restoredGroup.escapeRoomPageIndex ?? 0);
				const restoredAnswers = session.answers && typeof session.answers === "object"
					? session.answers
					: (restoredGroup.escapeRoomAnswers || {});
				dispatch(setGroup({
					...restoredGroup,
					escapeRoomPageIndex: pageIdx,
					escapeRoomAnswers: restoredAnswers,
					lastVisited: ROUTES.ESCAPE_ROOM,
				}));
				setPageIndex(pageIdx);
				setAnswers(restoredAnswers);
			} catch (_) {
				/* sesión corrupta o red */
			}
		})();
	}, [dispatch, game?._id, group?._id, group?.finished]);

	const [quizzes, setQuizzes] = useState({});
	const [scale, setScale] = useState(1);
	const [loading, setLoading] = useState(false);
	const [playViewport, setPlayViewport] = useState(() =>
		typeof window !== "undefined" ? pickErViewportFromSize(window.innerWidth, window.innerHeight) : "desktop"
	);
	/** Debounce: en móvil el alto cambia con la barra de URL y re-disparaba viewport → se reiniciaban los timers de “tras X s”. */
	useEffect(() => {
		const apply = () => {
			const next = pickErViewportFromSize(window.innerWidth, window.innerHeight);
			setPlayViewport((prev) => (prev === next ? prev : next));
		};
		apply();
		let debounceTimer = null;
		const onResize = () => {
			if (debounceTimer) clearTimeout(debounceTimer);
			debounceTimer = setTimeout(apply, 320);
		};
		window.addEventListener("resize", onResize);
		if (typeof window !== "undefined" && window.visualViewport) {
			window.visualViewport.addEventListener("resize", onResize);
		}
		return () => {
			if (debounceTimer) clearTimeout(debounceTimer);
			window.removeEventListener("resize", onResize);
			if (typeof window !== "undefined" && window.visualViewport) {
				window.visualViewport.removeEventListener("resize", onResize);
			}
		};
	}, []);
	const playRootRef = useRef(null);
	const containerRef = useRef(null);

	const currentPage = playablePages[pageIndex] || playablePages[0];

	useEffect(() => {
		if (!game?._id || !group?._id) return;
		if (group.finished) {
			clearErPlaySession();
			return;
		}
		saveErPlaySession({
			gameId: String(game._id),
			groupId: String(group._id),
			pageIndex,
			pageId: currentPage?.pageId || currentPage?._id || null,
			answers,
			score: group.score,
			time: group.time,
		});
	}, [game?._id, group?._id, group?.finished, group?.score, group?.time, pageIndex, currentPage?.pageId, currentPage?._id, answers]);

	/** Referencia estable entre renders: si no, el efecto de botones "tras X s" se reinicia cada frame y en móvil el botón nunca aparece. */
	const elements = useMemo(
		() => getElementsForViewport(currentPage, playViewport),
		[currentPage, playViewport]
	);
	const afterSecondsTimerKey = useMemo(() => afterSecondsButtonsDependencyKey(elements), [elements]);
	const hideAfterTimerKey = useMemo(() => hideAfterButtonsDependencyKey(elements), [elements]);
	const timerRunsOnCurrentPage = currentPage?.timerRuns !== false;
	const gameTimeoutSeconds = Number.isFinite(Number(game?.escapeRoom?.gameTimeSeconds))
		? Math.max(1, Math.round(Number(game.escapeRoom.gameTimeSeconds)))
		: GAME_TIME_SECS;

	const initialPlayHudTime = (() => {
		const saved = getTimer();
		const n = saved !== null ? Number(saved) : parseInt(group?.time || 0, 10);
		return Number.isFinite(n) ? n : 0;
	})();
	const playHudTimeRef = useRef(initialPlayHudTime);
	const timerMinElRef = useRef(null);
	const timerSecElRef = useRef(null);
	const terminalNavDoneRef = useRef(false);
	const playTimerRestoredRef = useRef(false);
	const groupSyncRef = useRef(group);

	const syncTimerHudDom = useCallback(() => {
		const t = playHudTimeRef.current;
		const finished = !!groupSyncRef.current?.finished;
		if (timerMinElRef.current) {
			timerMinElRef.current.textContent = formatMinutes(t, gameTimeoutSeconds, finished);
		}
		if (timerSecElRef.current) {
			timerSecElRef.current.textContent = formatSeconds(t, gameTimeoutSeconds, finished);
		}
	}, [gameTimeoutSeconds]);

	useEffect(() => {
		terminalNavDoneRef.current = false;
		playTimerRestoredRef.current = false;
	}, [group?._id]);

	useEffect(() => {
		groupSyncRef.current = group;
		syncTimerHudDom();
	}, [group, syncTimerHudDom]);

	useLayoutEffect(() => {
		syncTimerHudDom();
	}, [syncTimerHudDom]);

	useEffect(() => {
		if (!group?._id || group.finished || playTimerRestoredRef.current) return;
		if (getTimer() === null) {
			updateTimer(Number(group.time) || 0);
			setLastTickNow();
		}
		const saved = getTimer();
		const n = saved !== null ? Number(saved) : Number(group.time) || 0;
		if (Number.isFinite(n)) {
			playHudTimeRef.current = n;
			syncTimerHudDom();
		}
		playTimerRestoredRef.current = true;
	}, [group?._id, group?.finished, group?.time, syncTimerHudDom]);

	useEffect(() => {
		if (getTimer() !== null && !getLastTick()) setLastTickNow();
	}, []);

	const handlePlayTimeout = useCallback(() => {
		if (terminalNavDoneRef.current) return;
		const g = groupSyncRef.current;
		const playTime = playHudTimeRef.current;
		if (
			playTime < gameTimeoutSeconds ||
			!g?._id ||
			g?.lastVisited === ROUTES.GAMEOVER ||
			game?.demo
		) {
			return;
		}
		terminalNavDoneRef.current = true;
		dispatch(updateTime(playTime));
		updateStats({
			...g,
			time: playTime,
			lastVisited: ROUTES.GAMEOVER,
			finished: true,
		});
		try {
			const gid = String(g?.game?._id || g?.game || "");
			const groupId = String(g?._id || "");
			if (gid && groupId) {
				bumpSessionKpi("finish");
				postKpiEvent({
					gameId: gid,
					groupId,
					eventType: "finish",
					reason: "timeout",
					totalScoreSnapshot: Number(g?.score ?? null),
					timestamp: new Date().toISOString(),
				}).catch(() => {});
			}
		} catch (_) {}
		navigate(ROUTES.GAMEOVER);
	}, [dispatch, game?.demo, gameTimeoutSeconds, navigate]);

	const handlePlayTimerTick = useCallback(() => {
		if (getTimer() === null) return;
		if (!timerRunsOnCurrentPage) {
			setLastTickNow();
			return;
		}
		const now = Date.now();
		const prevTick = getLastTick() ?? now;
		const deltaMs = now - prevTick;
		const deltaSec = Math.floor(deltaMs / 1000);
		if (deltaSec <= 0) return;
		const next = playHudTimeRef.current + deltaSec;
		playHudTimeRef.current = next;
		updateTimer(next);
		setLastTick(prevTick + deltaSec * 1000);
		syncTimerHudDom();
		handlePlayTimeout();
	}, [timerRunsOnCurrentPage, syncTimerHudDom, handlePlayTimeout]);

	useTimer(() => {
		handlePlayTimerTick();
	}, 1000);

	useEffect(() => {
		const onVis = () => {
			if (document.visibilityState === "visible") handlePlayTimerTick();
		};
		document.addEventListener("visibilitychange", onVis);
		return () => document.removeEventListener("visibilitychange", onVis);
	}, [handlePlayTimerTick]);

	/** Sincroniza el tiempo de juego al servidor para ranking/analítica en vivo (el cliente sí avanza cada segundo). */
	useEffect(() => {
		if (!group?._id || group.finished || game?.demo) return undefined;
		const tick = () => {
			const g = groupSyncRef.current;
			const t = Number(playHudTimeRef.current) || 0;
			if (!g?._id || g.finished) return;
			const capped = Math.min(gameTimeoutSeconds, Math.max(0, t));
			updateStats({ ...g, time: capped }).catch(() => {});
		};
		const id = window.setInterval(tick, 10000);
		tick();
		return () => window.clearInterval(id);
	}, [group?._id, group?.finished, game?.demo, gameTimeoutSeconds]);

	useEffect(() => {
		if (terminalNavDoneRef.current) return;
		if (group?.finished) {
			terminalNavDoneRef.current = true;
			navigate(group?.lastVisited || ROUTES.RESULTS);
		}
	}, [group, navigate]);

	const playDim = getErViewportDimensions(playViewport);
	const [afterSecondsReady, setAfterSecondsReady] = useState(() => new Set());
	/**
	 * Botones “tras X s”: usar tiempo absoluto + intervalo + visibilitychange.
	 * En móvil, setTimeout puede ir muy rezagado o el viewport saltaba y se limpiaba el efecto antes de tiempo.
	 */
	useEffect(() => {
		const buttons = (elements || []).filter(
			(e) => e?.type === "button" && String(e?.content?.showWhen || "") === "afterSeconds"
		);
		setAfterSecondsReady(new Set());
		if (!buttons.length) return undefined;

		const pageEnterMs = Date.now();
		const deadlines = new Map();
		for (const btn of buttons) {
			const sec = Math.max(0, Number(btn.content?.showAfterSeconds) || 0);
			deadlines.set(String(btn.elementId), pageEnterMs + sec * 1000);
		}

		const flush = () => {
			const now = Date.now();
			setAfterSecondsReady((prev) => {
				const next = new Set(prev);
				let changed = false;
				deadlines.forEach((deadline, id) => {
					if (now >= deadline && !next.has(id)) {
						next.add(id);
						changed = true;
					}
				});
				return changed ? next : prev;
			});
		};

		flush();
		const intervalId = window.setInterval(flush, 280);
		const onVis = () => {
			if (document.visibilityState === "visible") flush();
		};
		document.addEventListener("visibilitychange", onVis);

		return () => {
			window.clearInterval(intervalId);
			document.removeEventListener("visibilitychange", onVis);
		};
	}, [pageIndex, afterSecondsTimerKey]);
	const [hideAfterExpired, setHideAfterExpired] = useState(() => new Set());
	const hideAfterShownAtRef = useRef(new Map());

	const baseVisibleButtonIds = useMemo(() => {
		const quizIds = (elements || []).filter((e) => e.type === "quiz").map((e) => e.elementId);
		const allQuizzesOnPageCompleted = quizIds.length === 0 || quizIds.every((id) => {
			const a = answers[id];
			return !!(a && a.done);
		});
		const activatedSet = activatedButtonsByPage[String(pageIndex)] || new Set();
		const pageButtons = (elements || []).filter((e) => e.type === "button");
		const s = new Set();
		for (const el of pageButtons) {
			const showWhen = el.content?.showWhen || "always";
			let visible = true;
			if (showWhen === "quizCompleted") visible = allQuizzesOnPageCompleted;
			else if (showWhen === "allButtonsActivated") {
				const others = pageButtons.filter((b) => b.elementId !== el.elementId);
				visible = others.length === 0 || others.every((b) => activatedSet.has(b.elementId));
			} else if (showWhen === "afterSeconds") {
				visible = afterSecondsReady.has(String(el.elementId));
			}
			if (visible) s.add(String(el.elementId));
		}
		return s;
	}, [elements, answers, activatedButtonsByPage, pageIndex, afterSecondsReady]);

	const baseVisibleButtonKey = useMemo(
		() => [...baseVisibleButtonIds].sort().join("|"),
		[baseVisibleButtonIds]
	);

	useEffect(() => {
		setHideAfterExpired(new Set());
		hideAfterShownAtRef.current = new Map();
	}, [pageIndex, hideAfterTimerKey]);

	useEffect(() => {
		const buttons = (elements || []).filter(
			(e) => e?.type === "button" && Math.max(0, Number(e?.content?.hideAfterSeconds) || 0) > 0
		);
		if (!buttons.length) {
			setHideAfterExpired(new Set());
			return undefined;
		}

		const now = Date.now();
		for (const btn of buttons) {
			const id = String(btn.elementId);
			if (baseVisibleButtonIds.has(id) && !hideAfterShownAtRef.current.has(id)) {
				hideAfterShownAtRef.current.set(id, now);
			}
		}

		const flush = () => {
			const t = Date.now();
			setHideAfterExpired((prev) => {
				const next = new Set(prev);
				let changed = false;
				for (const btn of buttons) {
					const id = String(btn.elementId);
					if (next.has(id)) continue;
					const shownAt = hideAfterShownAtRef.current.get(id);
					if (shownAt == null) continue;
					const sec = Math.max(0, Number(btn.content?.hideAfterSeconds) || 0);
					if (sec > 0 && t >= shownAt + sec * 1000 + BUTTON_HIDE_EXIT_MS) {
						next.add(id);
						changed = true;
					}
				}
				return changed ? next : prev;
			});
		};

		flush();
		const intervalId = window.setInterval(flush, 280);
		const onVis = () => {
			if (document.visibilityState === "visible") flush();
		};
		document.addEventListener("visibilitychange", onVis);
		return () => {
			window.clearInterval(intervalId);
			document.removeEventListener("visibilitychange", onVis);
		};
	}, [pageIndex, hideAfterTimerKey, baseVisibleButtonKey, elements, baseVisibleButtonIds]);

	const modalElements = modalContent?.design?.elements || [];
	const modalAfterSecondsTimerKey = useMemo(() => afterSecondsButtonsDependencyKey(modalElements), [modalElements]);
	const modalHideAfterTimerKey = useMemo(() => hideAfterButtonsDependencyKey(modalElements), [modalElements]);
	const [modalAfterSecondsReady, setModalAfterSecondsReady] = useState(() => new Set());
	const [modalHideAfterExpired, setModalHideAfterExpired] = useState(() => new Set());
	const modalHideAfterShownAtRef = useRef(new Map());

	useEffect(() => {
		if (modalContent) {
			if (!modalWasOpenRef.current) {
				modalWasOpenRef.current = true;
				setModalEntryAnimKey((k) => k + 1);
			}
		} else {
			modalWasOpenRef.current = false;
		}
	}, [modalContent]);

	useEffect(() => {
		const buttons = (modalElements || []).filter(
			(e) => e?.type === "button" && String(e?.content?.showWhen || "") === "afterSeconds"
		);
		setModalAfterSecondsReady(new Set());
		if (!modalContent || !buttons.length) return undefined;

		const enterMs = Date.now();
		const deadlines = new Map();
		for (const btn of buttons) {
			const sec = Math.max(0, Number(btn.content?.showAfterSeconds) || 0);
			deadlines.set(String(btn.elementId), enterMs + sec * 1000);
		}
		const flush = () => {
			const now = Date.now();
			setModalAfterSecondsReady((prev) => {
				const next = new Set(prev);
				let changed = false;
				deadlines.forEach((deadline, id) => {
					if (now >= deadline && !next.has(id)) {
						next.add(id);
						changed = true;
					}
				});
				return changed ? next : prev;
			});
		};
		flush();
		const intervalId = window.setInterval(flush, 280);
		return () => window.clearInterval(intervalId);
	}, [modalEntryAnimKey, modalAfterSecondsTimerKey, modalContent, modalElements]);

	const modalBaseVisibleButtonIds = useMemo(() => {
		const quizIds = (modalElements || []).filter((e) => e.type === "quiz").map((e) => e.elementId);
		const allQuizzesCompleted = quizIds.length === 0 || quizIds.every((id) => {
			const a = answers[id];
			return !!(a && a.done);
		});
		const activatedSet = activatedButtonsByPage[String(pageIndex)] || new Set();
		const modalButtons = (modalElements || []).filter((e) => e.type === "button");
		const s = new Set();
		for (const el of modalButtons) {
			const showWhen = el.content?.showWhen || "always";
			let visible = true;
			if (showWhen === "quizCompleted") visible = allQuizzesCompleted;
			else if (showWhen === "allButtonsActivated") {
				const others = modalButtons.filter((b) => b.elementId !== el.elementId);
				visible = others.length === 0 || others.every((b) => activatedSet.has(b.elementId));
			} else if (showWhen === "afterSeconds") {
				visible = modalAfterSecondsReady.has(String(el.elementId));
			}
			if (visible) s.add(String(el.elementId));
		}
		return s;
	}, [modalElements, answers, activatedButtonsByPage, pageIndex, modalAfterSecondsReady]);

	const modalBaseVisibleButtonKey = useMemo(
		() => [...modalBaseVisibleButtonIds].sort().join("|"),
		[modalBaseVisibleButtonIds]
	);

	useEffect(() => {
		setModalHideAfterExpired(new Set());
		modalHideAfterShownAtRef.current = new Map();
	}, [modalEntryAnimKey, modalHideAfterTimerKey]);

	useEffect(() => {
		const buttons = (modalElements || []).filter(
			(e) => e?.type === "button" && Math.max(0, Number(e?.content?.hideAfterSeconds) || 0) > 0
		);
		if (!modalContent || !buttons.length) {
			setModalHideAfterExpired(new Set());
			return undefined;
		}
		const now = Date.now();
		for (const btn of buttons) {
			const id = String(btn.elementId);
			if (modalBaseVisibleButtonIds.has(id) && !modalHideAfterShownAtRef.current.has(id)) {
				modalHideAfterShownAtRef.current.set(id, now);
			}
		}
		const flush = () => {
			const t = Date.now();
			setModalHideAfterExpired((prev) => {
				const next = new Set(prev);
				let changed = false;
				for (const btn of buttons) {
					const id = String(btn.elementId);
					if (next.has(id)) continue;
					const shownAt = modalHideAfterShownAtRef.current.get(id);
					if (shownAt == null) continue;
					const sec = Math.max(0, Number(btn.content?.hideAfterSeconds) || 0);
					if (sec > 0 && t >= shownAt + sec * 1000 + BUTTON_HIDE_EXIT_MS) {
						next.add(id);
						changed = true;
					}
				}
				return changed ? next : prev;
			});
		};
		flush();
		const intervalId = window.setInterval(flush, 280);
		return () => window.clearInterval(intervalId);
	}, [modalEntryAnimKey, modalHideAfterTimerKey, modalBaseVisibleButtonKey, modalElements, modalBaseVisibleButtonIds, modalContent]);

	const isRegisteredGroup = !!group?._id;

	// Si el grupo ya está registrado, saltar la página de registro dentro del play.
	useEffect(() => {
		if (!isRegistrationPage(currentPage) || !isRegisteredGroup) return;
		const forwardIdx = playablePages.findIndex((p, i) => i > pageIndex && !isRegistrationPage(p));
		const fallbackIdx = playablePages.findIndex((p) => !isRegistrationPage(p));
		const nextIdx = forwardIdx >= 0 ? forwardIdx : fallbackIdx;
		if (nextIdx >= 0 && nextIdx !== pageIndex) {
			// Misma señal que al pasar de página con botón: remontar el wrapper y disparar transición + animaciones de entrada.
			const pg = playablePages[pageIndex];
			setTransitionType(normalizePageTransitionForPlay(pg?.transitionToNext || "none"));
			setTransitionDuration(pg?.transitionDuration ?? 350);
			setTransitionKey((k) => k + 1);
			setPageIndex(nextIdx);
			if (group?._id && Number(group?.escapeRoomPageIndex) !== nextIdx) {
				const nextPage = playablePages[nextIdx];
				dispatch(setGroup({
					...group,
					escapeRoomPageIndex: nextIdx,
					lastVisited: ROUTES.ESCAPE_ROOM,
					escapeRoomPageId: nextPage?.pageId || nextPage?._id,
				}));
			}
		}
	}, [currentPage, isRegisteredGroup, playablePages, pageIndex, group?._id, group?.escapeRoomPageIndex, dispatch]);

	// Si el grupo NO está registrado y la página actual es de registro,
	// ir al formulario /register y guardar el índice de retorno.
	useEffect(() => {
		if (!isRegistrationPage(currentPage) || isRegisteredGroup) return;
		let returnIdx = playablePages.findIndex((p, i) => i > pageIndex && !isRegistrationPage(p));
		if (returnIdx < 0) returnIdx = playablePages.findIndex((p) => !isRegistrationPage(p));
		try {
			if (returnIdx >= 0) localStorage.setItem("erRegisterReturnIndex", String(returnIdx));
			else localStorage.removeItem("erRegisterReturnIndex");
			localStorage.setItem("erForceRegisterFlow", "1");
		} catch (_) {}
		clearItem("groupTable");
		const gameId = game?._id;
		const search = gameId ? `?game=${encodeURIComponent(String(gameId))}` : "";
		navigate(
			{ pathname: ROUTES.REGISTER, search: search || undefined },
			{ replace: true }
		);
	}, [currentPage, isRegisteredGroup, playablePages, pageIndex, game?._id, navigate]);

	// Fuentes base + customFonts (nombres o URLs de Google Fonts).
	useEffect(() => {
		const urls = collectFontStylesheetUrls(escapeRoom?.customFonts);
		return injectEscapeRoomFontLinks(urls);
	}, [escapeRoom?.customFonts]);

	useEffect(() => {
		ensureTextEffectStylesInjected();
	}, []);

	// Clamp page index
	useEffect(() => {
		if (playablePages.length > 0 && pageIndex >= playablePages.length) {
			setPageIndex(playablePages.length - 1);
		}
	}, [playablePages.length, pageIndex]);

	// Fetch quizzes for quiz elements on current page (incl. modales)
	useEffect(() => {
		const quizIds = [...collectQuizIdsFromElements(elements || [])];
		quizIds.forEach((id) => {
			if (!quizzes[id]) {
				getQuizById(id)
					.then((res) => {
						if (res.status === 200) setQuizzes((prev) => ({ ...prev, [id]: res.data }));
					})
					.catch(() => {});
			}
		});
	}, [currentPage?.pageId, elements]);

	// Fetch quizzes cuando se abre un modal con preguntas
	useEffect(() => {
		const modalEls = [
			...(modalContent?.design?.elements || []),
			...(hintDesignModal?.design?.elements || []),
		];
		if (!modalEls.length) return;
		const quizIds = [...collectQuizIdsFromElements(modalEls)];
		quizIds.forEach((id) => {
			if (!quizzes[id]) {
				getQuizById(id)
					.then((res) => {
						if (res.status === 200) setQuizzes((prev) => ({ ...prev, [id]: res.data }));
					})
					.catch(() => {});
			}
		});
	}, [modalContent, hintDesignModal]);

	// Precarga medios y quizzes de páginas siguientes / destinos de botones.
	useEffect(() => {
		if (!playablePages.length) return;
		const current = playablePages[pageIndex];
		if (current) {
			prefetchEscapeRoomPage(current, {
				fetchQuiz: (id) =>
					getQuizById(id).then((res) => {
						if (res.status === 200 && res.data) {
							setQuizzes((prev) => (prev[id] ? prev : { ...prev, [id]: res.data }));
						}
						return res;
					}),
			});
		}
		const targets = new Set(collectOutgoingPageIndicesFromPage(playablePages, pageIndex));
		if (pageIndex + 1 < playablePages.length) targets.add(pageIndex + 1);
		targets.forEach((idx) => {
			if (idx === pageIndex || idx < 0 || idx >= playablePages.length) return;
			prefetchEscapeRoomPage(playablePages[idx], {
				fetchQuiz: (id) =>
					getQuizById(id).then((res) => {
						if (res.status === 200 && res.data) {
							setQuizzes((prev) => (prev[id] ? prev : { ...prev, [id]: res.data }));
						}
						return res;
					}),
			});
		});
	}, [pageIndex, playablePages]);

	// Escala al máximo área visible: en móvil/tablet usa visualViewport (barra URL iOS) y márgenes mínimos.
	const updateScale = useCallback(() => {
		if (!containerRef.current) return;
		const { width: cw, height: ch } = getErViewportDimensions(playViewport);
		if (!cw || !ch) return;
		const rect = containerRef.current.getBoundingClientRect();
		const isMobile = playViewport === "mobile";
		const isTablet = playViewport === "tablet";
		const isDesktop = playViewport === "desktop";
		let vw = rect.width;
		let vh = rect.height;
		if (typeof window !== "undefined" && window.visualViewport && (isMobile || isTablet)) {
			const vp = window.visualViewport;
			vw = Math.min(rect.width, vp.width);
			vh = Math.min(rect.height, vp.height);
		}
		// Móvil/tablet: "contain" (sin recorte). Escritorio: "cover" (sin bandas; puede recortar bordes del lienzo).
		const pad = isMobile ? 2 : isTablet ? 6 : 0;
		const availW = Math.max(80, vw - pad * 2);
		const availH = Math.max(80, vh - pad * 2);
		const maxZoom = isMobile ? 8 : isTablet ? 4 : 3;
		const sx = availW / cw;
		const sy = availH / ch;
		let s;
		if (isDesktop) {
			s = Math.min(maxZoom, Math.max(sx, sy));
		} else {
			s = Math.min(sx, sy, maxZoom);
		}
		setScale(Math.max(0.45, s));
	}, [playViewport]);

	useLayoutEffect(() => {
		updateScale();
	}, [playViewport, updateScale, pageIndex, transitionKey]);

	useEffect(() => {
		updateScale();
		const el = containerRef.current;
		const ro = el && new ResizeObserver(() => updateScale());
		if (ro && el) ro.observe(el);
		const vv = typeof window !== "undefined" ? window.visualViewport : null;
		const onVv = () => updateScale();
		if (vv && (playViewport === "mobile" || playViewport === "tablet")) {
			vv.addEventListener("resize", onVv);
			vv.addEventListener("scroll", onVv);
		}
		return () => {
			ro?.disconnect();
			if (vv) {
				vv.removeEventListener("resize", onVv);
				vv.removeEventListener("scroll", onVv);
			}
		};
	}, [playViewport, updateScale]);

	// Tras volver de /register el contenedor se monta de nuevo: recalcular escala cuando ya hay grupo.
	useEffect(() => {
		if (!isRegisteredGroup) return undefined;
		const raf = requestAnimationFrame(() => updateScale());
		const t = window.setTimeout(() => updateScale(), 80);
		return () => {
			cancelAnimationFrame(raf);
			clearTimeout(t);
		};
	}, [isRegisteredGroup, pageIndex, updateScale]);

	// Pantalla completa del navegador al entrar al escape room
	useEffect(() => {
		if (!escapeRoom || playablePages.length === 0) return;
		if (typeof document === "undefined") return;

		let cancelled = false;
		const tryEnter = () => {
			if (cancelled) return;
			const el = playRootRef.current;
			if (!el || document.fullscreenElement) return;
			const req =
				el.requestFullscreen ||
				el.webkitRequestFullscreen ||
				el.mozRequestFullScreen ||
				el.msRequestFullscreen;
			if (typeof req !== "function") return;
			Promise.resolve(req.call(el)).catch(() => {});
		};

		const raf = requestAnimationFrame(() => requestAnimationFrame(tryEnter));

		return () => {
			cancelled = true;
			cancelAnimationFrame(raf);
			// No forzar exitFullscreen al desmontar (p. ej. al ir a /register): el elemento fullscreen deja de existir
			// y el usuario puede seguir en flujo; Register puede volver a solicitar pantalla completa.
		};
	}, [escapeRoom, playablePages.length]);

	// Puntos por aterrizar: aplicar una vez por página (actual al cargar + al navegar)
	useEffect(() => {
		if (!group?._id || !playablePages.length || appliedInitialLandingRef.current) return;
		const idx = Number.isFinite(group.escapeRoomPageIndex) ? group.escapeRoomPageIndex : 0;
		if (appliedPointsForPageRef.current.has(idx)) {
			appliedInitialLandingRef.current = true;
			return;
		}
		const page = playablePages[idx];
		const pts = page?.pointsOnLand ?? 0;
		if (!Number.isFinite(pts) || pts === 0) {
			appliedInitialLandingRef.current = true;
			return;
		}
		appliedInitialLandingRef.current = true;
		patchScoreDelta(group._id, pts)
			.then((res) => {
				if (res?.data && res.status === 200) {
					appliedPointsForPageRef.current.add(idx);
					dispatch(setGroup({
						...res.data,
						escapeRoomPageIndex: idx,
						lastVisited: ROUTES.ESCAPE_ROOM,
						escapeRoomAnswers: group.escapeRoomAnswers,
					}));
					updateStats({
						...res.data,
						escapeRoomPageIndex: idx,
						lastVisited: ROUTES.ESCAPE_ROOM,
						escapeRoomAnswers: group.escapeRoomAnswers,
					});
				}
			})
			.catch(() => {});
	}, [group?._id, group?.escapeRoomPageIndex, group?.escapeRoomAnswers, playablePages, dispatch]);

	const persistPageAndVisit = useCallback(
		async (newPageIndex) => {
			let safePageIndex = newPageIndex;
			if (isRegisteredGroup && isRegistrationPage(playablePages[safePageIndex])) {
				let i = safePageIndex + 1;
				while (i < playablePages.length && isRegistrationPage(playablePages[i])) i += 1;
				if (i < playablePages.length) safePageIndex = i;
			}
			const destinationPage = playablePages[safePageIndex];
			const pointsOnLand = destinationPage?.pointsOnLand ?? 0;
			let g = { ...group, escapeRoomPageIndex: safePageIndex, lastVisited: ROUTES.ESCAPE_ROOM };
			const alreadyApplied = appliedPointsForPageRef.current.has(safePageIndex);
			if (!alreadyApplied && Number.isFinite(pointsOnLand) && pointsOnLand !== 0 && group?._id) {
				try {
					const res = await patchScoreDelta(group._id, pointsOnLand);
					if (res?.data && res.status === 200) {
						appliedPointsForPageRef.current.add(safePageIndex);
						g = {
							...res.data,
							escapeRoomPageIndex: safePageIndex,
							lastVisited: ROUTES.ESCAPE_ROOM,
							escapeRoomAnswers: group.escapeRoomAnswers,
						};
					} else {
						g = { ...g, score: (group?.score ?? 0) + pointsOnLand };
						appliedPointsForPageRef.current.add(safePageIndex);
					}
				} catch (_) {
					g = { ...g, score: (group?.score ?? 0) + pointsOnLand };
					appliedPointsForPageRef.current.add(safePageIndex);
				}
			}
			dispatch(setGroup(g));
			await updateStats(g);
			// KPI: page_view (best-effort, no bloquear gameplay)
			try {
				const gid = String(group?.game?._id || group?.game || "");
				const page = playablePages[safePageIndex];
				const pageId = String(page?.pageId || page?._id || "");
				if (gid && group?._id) {
					bumpSessionKpi("page_view");
					postKpiEvent({
						gameId: gid,
						groupId: String(group._id),
						eventType: "page_view",
						pageId,
						pageIndex: safePageIndex,
						deltaScore: !alreadyApplied ? pointsOnLand : 0,
						totalScoreSnapshot: Number(g?.score ?? null),
						timestamp: new Date().toISOString(),
					}).catch(() => {});
				}
			} catch (_) {}
		},
		[group, dispatch, playablePages, isRegisteredGroup]
	);

	const invertTransition = (t) => {
		if (t === "slideLeft") return "slideRight";
		if (t === "slideRight") return "slideLeft";
		if (t === "slideUp") return "slideDown";
		if (t === "slideDown") return "slideUp";
		if (t === "flashExit") return "flashEnter";
		if (t === "flashEnter") return "flashExit";
		if (t === "flashBoth") return "flashBoth";
		return t;
	};

	const runPageChange = useCallback((toIndex, { persist = true } = {}) => {
		if (toIndex < 0 || toIndex >= playablePages.length || toIndex === pageIndex) return;
		if (pageNavTimerRef.current) {
			clearTimeout(pageNavTimerRef.current);
			pageNavTimerRef.current = null;
		}
		const { needsExitFlash, playTransitionType: animType, durationMs } = computePlayPageTransition({
			fromIndex: pageIndex,
			toIndex,
			pages: playablePages,
		});
		const finish = () => {
			setExitFlash(null);
			setTransitionType(normalizePageTransitionForPlay(animType));
			setTransitionDuration(durationMs);
			setTransitionKey((k) => k + 1);
			setPageIndex(toIndex);
			if (persist) persistPageAndVisit(toIndex);
		};
		if (needsExitFlash) {
			setExitFlash({ durationMs, key: Date.now() });
			pageNavTimerRef.current = setTimeout(finish, durationMs);
		} else {
			finish();
		}
	}, [pageIndex, playablePages, persistPageAndVisit]);

	useEffect(() => () => {
		if (pageNavTimerRef.current) clearTimeout(pageNavTimerRef.current);
	}, []);

	const handleButtonAction = useCallback(
		(el) => {
			// Registrar que este botón fue activado (para onlyOnce y allButtonsActivated)
			if (el?.elementId != null) {
				setActivatedButtonsByPage((prev) => {
					const key = String(pageIndex);
					const next = { ...prev };
					next[key] = new Set(prev[key] || []);
					next[key].add(el.elementId);
					return next;
				});
				// KPI: button_click (best-effort)
				try {
					const gid = String(group?.game?._id || group?.game || "");
					const page = playablePages[pageIndex];
					const pageId = String(page?.pageId || page?._id || "");
					if (gid && group?._id) {
						bumpSessionKpi("button_click");
						postKpiEvent({
							gameId: gid,
							groupId: String(group._id),
							eventType: "button_click",
							pageId,
							pageIndex,
							elementId: String(el.elementId),
							totalScoreSnapshot: Number(group?.score ?? null),
							timestamp: new Date().toISOString(),
						}).catch(() => {});
					}
				} catch (_) {}
			}
			const action = el?.content?.action;
			if (action === "none") {
				return;
			}
			if (action === "closeModal") {
				setModalContent(null);
				return;
			}
			if (action === "nextPage") {
				setModalContent(null);
				if (pageIndex < playablePages.length - 1) {
					let next = pageIndex + 1;
					while (isRegisteredGroup && next < playablePages.length && isRegistrationPage(playablePages[next])) next += 1;
					if (next >= playablePages.length) {
						const g = { ...group, lastVisited: ROUTES.RESULTS };
						dispatch(setGroup(g));
						updateStats(g);
						navigate(ROUTES.RESULTS);
						return;
					}
					suppressBranchAlternatesWeb(playablePages[next]?.scenarioId, playablePages, setSuppressedScenarioIds);
					runPageChange(next);
				} else {
					// Fin del escape room -> ir a pantalla final o resultados
					const g = { ...group, lastVisited: ROUTES.RESULTS };
					dispatch(setGroup(g));
					updateStats(g);
					navigate(ROUTES.RESULTS);
				}
			} else if (action === "prevPage" && pageIndex > 0) {
				setModalContent(null);
				let prev = pageIndex - 1;
				while (isRegisteredGroup && prev >= 0 && isRegistrationPage(playablePages[prev])) prev -= 1;
				if (prev < 0) return;
				runPageChange(prev);
			} else if (action === "goToPage" && el?.content?.targetPageId) {
				setModalContent(null);
				let targetIdx = playablePages.findIndex(
					(p) => (p.pageId || p._id) === el.content.targetPageId
				);
				if (isRegisteredGroup && targetIdx >= 0 && isRegistrationPage(playablePages[targetIdx])) {
					let i = targetIdx + 1;
					while (i < playablePages.length && isRegistrationPage(playablePages[i])) i += 1;
					targetIdx = i < playablePages.length ? i : -1;
				}
				if (targetIdx >= 0 && targetIdx !== pageIndex) {
					suppressBranchAlternatesWeb(playablePages[targetIdx]?.scenarioId, playablePages, setSuppressedScenarioIds);
					runPageChange(targetIdx);
				}
			} else if (action === "openModal" && el?.content?.modalDesign) {
				const sizeDef = MODAL_SIZES[el.content?.modalSize || "medium"] || MODAL_SIZES.medium;
				setModalContent({
					design: el.content.modalDesign,
					width: sizeDef.width,
					height: sizeDef.height,
				});
			} else if (action === "addPoints") {
				const delta = Number(el?.content?.pointsDelta);
				if (!Number.isFinite(delta) || delta === 0) return;
				(async () => {
					let g = {
						...group,
						escapeRoomPageIndex: pageIndex,
						lastVisited: ROUTES.ESCAPE_ROOM,
					};
					if (group?._id) {
						try {
							const res = await patchScoreDelta(group._id, delta);
							if (res?.data && res.status === 200) {
								g = {
									...res.data,
									escapeRoomPageIndex: pageIndex,
									escapeRoomAnswers: group.escapeRoomAnswers,
									lastVisited: ROUTES.ESCAPE_ROOM,
								};
							} else {
								g = { ...g, score: (group?.score ?? 0) + delta };
							}
						} catch (_) {
							g = { ...g, score: (group?.score ?? 0) + delta };
						}
					} else {
						g = { ...g, score: (group?.score ?? 0) + delta };
					}
					dispatch(setGroup(g));
					await updateStats(g);
				})();
			}
		},
		[pageIndex, playablePages, group, dispatch, persistPageAndVisit, navigate, isRegisteredGroup, runPageChange]
	);

	const bgVideoAdvanceRef = useRef(false);
	useEffect(() => {
		bgVideoAdvanceRef.current = false;
	}, [pageIndex, currentPage?.pageId, currentPage?.backgroundImageUrl, currentPage?.advanceOnBackgroundVideoEnd]);

	const handleBackgroundVideoEnded = useCallback(() => {
		if (bgVideoAdvanceRef.current) return;
		const url = currentPage?.backgroundImageUrl ? resolveEscapeRoomMediaUrl(currentPage.backgroundImageUrl) : null;
		if (!currentPage?.advanceOnBackgroundVideoEnd || !url || !isEscapeRoomVideoUrl(url)) return;
		bgVideoAdvanceRef.current = true;
		handleButtonAction({ content: { action: "nextPage" } });
	}, [currentPage, handleButtonAction]);

	const handleBackgroundVideoError = useCallback(() => {
		const url = currentPage?.backgroundImageUrl ? resolveEscapeRoomMediaUrl(currentPage.backgroundImageUrl) : null;
		if (!currentPage?.advanceOnBackgroundVideoEnd || !url || !isEscapeRoomVideoUrl(url)) return;
		if (bgVideoAdvanceRef.current) return;
		bgVideoAdvanceRef.current = true;
		handleButtonAction({ content: { action: "nextPage" } });
	}, [currentPage, handleButtonAction]);

	const findPlayElementById = useCallback(
		(elementId) => {
			const fromPage = (elements || []).find((e) => e.elementId === elementId);
			if (fromPage) return fromPage;
			const fromModal = (modalContent?.design?.elements || []).find((e) => e.elementId === elementId);
			if (fromModal) return fromModal;
			const fromHint = (hintDesignModal?.design?.elements || []).find((e) => e.elementId === elementId);
			return fromHint || null;
		},
		[elements, modalContent, hintDesignModal]
	);

	const collectPlayElementsForHints = useCallback(() => {
		return [
			...(elements || []),
			...(modalContent?.design?.elements || []),
			...(hintDesignModal?.design?.elements || []),
		];
	}, [elements, modalContent, hintDesignModal]);

	const handleQuizOptionSelect = useCallback(
		async (elementId, optionIndex, correct) => {
			const el = findPlayElementById(elementId);
			const quizIdResolved = resolveQuizId(el?.content?.quizId);
			if (!quizIdResolved) return;

			const quiz = quizzes[quizIdResolved];
			const isSurvey = String(quiz?.gradingMode || "").toLowerCase() === "survey";
			const attemptMode = quiz?.attemptMode || (quiz?.allowMultipleAttempts ? "unlimited" : "single");
			const maxAttempts = Math.max(1, quiz?.maxAttempts ?? 3);
			const opts = quiz?.questions?.[0]?.options || [];
			const totalCorrectCount = isSurvey ? 1 : opts.filter((o) => o?.correct).length;
			const current = answers[elementId];
			const prevSelections = current?.selections || (current?.selectedIndex != null ? [{ optionIndex: current.selectedIndex, correct: current.correct }] : []);

			if (prevSelections.some((s) => s.optionIndex === optionIndex)) return;
			const done = current?.done;
			if (done) return;

			setLoading(true);
			const pointsCorrect = quiz?.pointsCorrect ?? 10;
			const pointsIncorrect = quiz?.pointsIncorrect ?? -5;
			const hintPenaltyOnPage = collectPlayElementsForHints()
				.filter((e) => e.type === "hint")
				.reduce((sum, h) => sum + (answers[h.elementId]?.hintPenaltyUsed || 0), 0);
			// La penalización por pista ya se aplicó al usar la pista (patchScoreDelta), no restar aquí
			const delta = isSurvey ? 0 : (correct ? pointsCorrect : pointsIncorrect);
			const answerCorrect = isSurvey ? true : !!correct;
			const optLabel = String(opts[optionIndex]?.text || t("COMMON.OPTION_N", { n: optionIndex + 1 })).slice(0, 500);

			try {
				const res = await postAnswer({
					scenario: null,
					quiz: quizIdResolved,
					group: group?._id,
					score: delta,
					time: getTimer() || 0,
					lastVisited: ROUTES.ESCAPE_ROOM,
					correct: answerCorrect,
				});

				if (res.status === 201 || res.status === 200) {
					const serverGroup = res.data?.group;
					const newSelections = [...prevSelections, { optionIndex, correct: answerCorrect, gradingMode: isSurvey ? "survey" : "scored" }];
					const correctSelectedCount = newSelections.filter((s) => s.correct).length;
					const newDone = isSurvey
						? attemptMode === "single"
							? true
							: attemptMode === "limited"
								? newSelections.length >= maxAttempts
								: newSelections.length >= 1
						: attemptMode === "single"
							? true
							: attemptMode === "limited"
								? correctSelectedCount >= totalCorrectCount || newSelections.length >= maxAttempts
								: correctSelectedCount >= totalCorrectCount;
					const totalPoints = isSurvey ? 0 : newSelections.reduce((sum, s) =>
						sum + (s.correct ? pointsCorrect : pointsIncorrect), 0);
					const incorrectCount = isSurvey ? 0 : newSelections.filter((s) => !s.correct).length;
					const newAnswers = {
						...answers,
						[elementId]: {
							selections: newSelections,
							done: newDone,
							hintPenaltyUsed: hintPenaltyOnPage,
						},
					};
					// Usar puntuación del servidor (postAnswer ya hace $inc); evita desfase con la UI local.
					const newScore = resolveGroupScoreFromApi(serverGroup, group, delta);
					const updatedGroup = {
						...(serverGroup || group),
						score: newScore,
						escapeRoomPageIndex: pageIndex,
						escapeRoomAnswers: newAnswers,
						lastVisited: ROUTES.ESCAPE_ROOM,
					};
					dispatch(setGroup(updatedGroup));
					setAnswers(newAnswers);
					await updateStats(updatedGroup);
					// KPI: quiz_answer (best-effort)
					try {
						const gid = String(updatedGroup?.game?._id || updatedGroup?.game || "");
						const page = playablePages[pageIndex];
						const pageId = String(page?.pageId || page?._id || "");
						if (gid && updatedGroup?._id) {
							bumpSessionKpi("quiz_answer");
							postKpiEvent({
								gameId: gid,
								groupId: String(updatedGroup._id),
								eventType: "quiz_answer",
								pageId,
								pageIndex,
								elementId: String(elementId),
								quizId: String(quizIdResolved),
								quizAttempt: newSelections.length,
								correct: answerCorrect,
								deltaScore: delta,
								totalScoreSnapshot: Number(updatedGroup?.score ?? null),
								timestamp: new Date().toISOString(),
								payload: {
									gradingMode: isSurvey ? "survey" : "scored",
									questionType: quiz?.questions?.[0]?.type || "multipleChoice",
									optionIndex,
									optionText: optLabel,
									answerKind: "choice",
								},
							}).catch(() => {});
						}
					} catch (_) {}
					setQuizFeedbackModal({
						correct: answerCorrect,
						delta,
						totalCorrect: totalCorrectCount,
						correctCount: correctSelectedCount,
						done: newDone,
						totalPoints,
						incorrectCount,
						selectionsCount: newSelections.length,
						surveyMode: isSurvey,
						surveySubmitMessage: String(quiz?.surveySubmitMessage || "").trim(),
						explanation: isSurvey ? null : (quiz?.questions?.[0]?.explanation?.trim() || null),
						theme: resolveQuizFeedbackThemeForElement(el, escapeRoom?.defaultQuizFeedbackTheme),
					});
				}
			} catch (err) {
				const code = err?.response?.data?.code;
				if (err?.response?.status === 409 && code === "E_DUPLICATE_ANSWER") {
					setAnswers((prev) => ({ ...prev, [elementId]: { selections: [...prevSelections, { optionIndex, correct: answerCorrect }], done: attemptMode === "single" || isSurvey, hintPenaltyUsed: hintPenaltyOnPage } }));
				} else {
					alertError({ error: err, customMessage: err?.response?.data?.message || t("PLAY.SAVE_ANSWER_ERROR") });
				}
			}
			setLoading(false);
		},
		[answers, findPlayElementById, collectPlayElementsForHints, group, quizzes, pageIndex, dispatch, playablePages]
	);

	const handleQuizCustomAnswer = useCallback(
		async (elementId, answerType, payload) => {
			const el = findPlayElementById(elementId);
			const quizId = resolveQuizId(el?.content?.quizId);
			if (!quizId || (answerType !== "match" && answerType !== "fillBlanks" && answerType !== "textAnswer")) return;
			const quiz = quizzes[quizId];
			const isSurvey = String(quiz?.gradingMode || "").toLowerCase() === "survey";
			const firstQ = quiz?.questions?.[0];
			const current = answers[elementId] || {};
			const attemptCount = (current.attemptCount || 0) + 1;
			let correctCount = 0;
			let totalCorrect = 0;
			let answerState = {};
			let selectionsCount = 0;
			if (answerType === "match") {
				const submittedPairs = Array.isArray(payload?.pairs) ? payload.pairs : [];
				const correctPairs = new Set((firstQ?.matchPairs || []).map(([l, r]) => `${l}:${r}`));
				correctCount = submittedPairs.filter(([l, r]) => correctPairs.has(`${l}:${r}`)).length;
				totalCorrect = (firstQ?.matchPairs || []).length;
				answerState = { pairs: submittedPairs, confirmed: true };
				selectionsCount = submittedPairs.length;
			} else if (answerType === "fillBlanks") {
				const submittedBlanks = Array.isArray(payload?.blanks) ? payload.blanks : [];
				const blankSegments = (firstQ?.segments || []).filter((s) => s?.type === "blank");
				totalCorrect = blankSegments.length;
				correctCount = submittedBlanks.filter((b) => {
					const seg = blankSegments.find((s) => s.blankId === b.blankId);
					if (!seg) return false;
					return String((seg.correctAnswer || "").trim()).toLowerCase() === String((b.answer || "").trim()).toLowerCase();
				}).length;
				answerState = { blanks: submittedBlanks, confirmed: true };
				selectionsCount = submittedBlanks.length;
			} else if (answerType === "textAnswer") {
				const submittedText = String(payload?.text || "").trim();
				totalCorrect = 1;
				correctCount = isSurvey || quizTextAnswersMatch(submittedText, firstQ?.correctAnswer || "") ? 1 : 0;
				answerState = { text: submittedText, confirmed: true };
				selectionsCount = submittedText ? 1 : 0;
			}
			const correct = isSurvey ? true : (totalCorrect > 0 && correctCount === totalCorrect);
			const pointsCorrect = quiz?.pointsCorrect ?? 10;
			const pointsIncorrect = quiz?.pointsIncorrect ?? -5;
			const incorrectCount = isSurvey ? 0 : Math.max(0, totalCorrect - correctCount);
			const delta = isSurvey ? 0 : (correct ? pointsCorrect : pointsIncorrect);
			const totalPoints = accumulateCustomQuizPoints({
				quiz,
				isSurvey,
				isCorrect: correct,
				pointsCorrect,
				pointsIncorrect,
				previousTotal: current.totalPoints ?? 0,
			});
			const done = resolveCustomQuizDone({ quiz, isSurvey, allCorrect: correct, attemptCount });
			answerState = { ...answerState, correct, done, attemptCount };
			setLoading(true);
			try {
				const res = await postAnswer({
					scenario: null,
					quiz: quizId,
					group: group?._id,
					score: delta,
					time: getTimer() || 0,
					lastVisited: ROUTES.ESCAPE_ROOM,
					correct,
				});
				if (res.status === 201 || res.status === 200) {
					const serverGroup = res.data?.group;
					const newAnswers = {
						...answers,
						[elementId]: {
							...answerState,
							totalPoints,
						},
					};
					const newScore = resolveGroupScoreFromApi(serverGroup, group, delta);
					const updatedGroup = {
						...(serverGroup || group),
						score: newScore,
						escapeRoomPageIndex: pageIndex,
						escapeRoomAnswers: newAnswers,
						lastVisited: ROUTES.ESCAPE_ROOM,
					};
					dispatch(setGroup(updatedGroup));
					setAnswers(newAnswers);
					await updateStats(updatedGroup);
					try {
						const gid = String(updatedGroup?.game?._id || updatedGroup?.game || "");
						const page = playablePages[pageIndex];
						const pageId = String(page?.pageId || page?._id || "");
						if (gid && updatedGroup?._id) {
							bumpSessionKpi("quiz_answer");
							postKpiEvent({
								gameId: gid,
								groupId: String(updatedGroup._id),
								eventType: "quiz_answer",
								pageId,
								pageIndex,
								elementId: String(elementId),
								quizId: String(quizId),
								quizAttempt: 1,
								correct,
								deltaScore: delta,
								totalScoreSnapshot: Number(updatedGroup?.score ?? null),
								timestamp: new Date().toISOString(),
								payload: {
									gradingMode: isSurvey ? "survey" : "scored",
									questionType: answerType,
									answerKind: answerType,
									pairs: answerType === "match" ? (answerState.pairs || []) : undefined,
									blanks: answerType === "fillBlanks" ? (answerState.blanks || []) : undefined,
									text: answerType === "textAnswer" ? (answerState.text || "") : undefined,
									correctCount,
									totalCorrect,
								},
							}).catch(() => {});
						}
					} catch (_) {}
					setQuizFeedbackModal({
						correct,
						delta,
						totalCorrect,
						correctCount,
						done,
						totalPoints,
						incorrectCount,
						selectionsCount,
						surveyMode: isSurvey,
						surveySubmitMessage: String(quiz?.surveySubmitMessage || "").trim(),
						explanation: isSurvey ? null : (firstQ?.explanation?.trim() || null),
						theme: resolveQuizFeedbackThemeForElement(el, escapeRoom?.defaultQuizFeedbackTheme),
					});
				}
			} catch (err) {
				alertError({ error: err, customMessage: err?.response?.data?.message || t("PLAY.SAVE_ANSWER_ERROR") });
			}
			setLoading(false);
		},
		[answers, findPlayElementById, group, quizzes, pageIndex, dispatch, playablePages, escapeRoom]
	);

	const handleHintUsed = useCallback(
		async (elementId, penalty) => {
			if (penalty <= 0) return;
			const newAnswers = {
				...answers,
				[elementId]: {
					...answers[elementId],
					hintPenaltyUsed: (answers[elementId]?.hintPenaltyUsed || 0) + penalty,
				},
			};
			setAnswers(newAnswers);
			let g = {
				...group,
				escapeRoomPageIndex: pageIndex,
				escapeRoomAnswers: newAnswers,
				lastVisited: ROUTES.ESCAPE_ROOM,
			};
			if (group?._id) {
				try {
					const res = await patchScoreDelta(group._id, -penalty);
					if (res?.data && res.status === 200) {
						g = { ...res.data, escapeRoomPageIndex: pageIndex, escapeRoomAnswers: newAnswers, lastVisited: ROUTES.ESCAPE_ROOM };
					} else {
						g = { ...g, score: (group?.score ?? 0) - penalty };
					}
				} catch (_) {
					g = { ...g, score: (group?.score ?? 0) - penalty };
				}
			}
			dispatch(setGroup(g));
			await updateStats(g);
		},
		[answers, group, pageIndex, dispatch]
	);

	const getModalSize = useCallback((sizeId) => {
		const s = MODAL_SIZES[sizeId || "medium"];
		return s ? { width: s.width, height: s.height } : { width: 640, height: 480 };
	}, []);

	const handleHintDesignOpen = useCallback((payload) => {
		if (!payload) return;
		if (payload.type === "confirm" && payload.design?.elements) {
			setHintDesignModal({
				type: "confirm",
				element: payload.element,
				design: payload.design,
				size: getModalSize(payload.size),
				penalty: payload.penalty ?? 0,
				hintText: payload.hintText || "",
				label: payload.label || t("COMMON.HINT"),
			});
		} else if (payload.type === "hint" && payload.design?.elements) {
			setHintDesignModal({
				type: "hint",
				element: payload.element,
				design: payload.design,
				size: getModalSize(payload.size),
				hintText: payload.hintText || "",
				label: payload.label || t("COMMON.HINT"),
			});
		} else if (payload.type === "simple") {
			setHintDesignModal({
				type: "simple",
				text: payload.hintText || "",
				label: payload.label || t("COMMON.HINT"),
			});
		}
	}, [getModalSize]);

	const handleHintModalButtonAction = useCallback((elem, hintModalData) => {
		const action = elem?.content?.action;
		if (action === "closeModal" || action === "cancelHint") {
			setHintDesignModal(null);
			return;
		}
		if (action === "confirmHint" && hintModalData?.type === "confirm") {
			if (hintModalData.penalty > 0 && hintModalData.element?.elementId) {
				handleHintUsed(hintModalData.element.elementId, hintModalData.penalty);
			}
			const el = hintModalData.element;
			const c = el?.content || {};
			const hintDesign = c.hintModalDesign;
			const hintSizeId = c.hintModalSize || "medium";
			if (hintDesign?.elements?.length) {
				setHintDesignModal({
					type: "hint",
					element: el,
					design: hintDesign,
					size: getModalSize(hintSizeId),
					hintText: c.hintText || "",
					label: c.label || t("COMMON.HINT"),
				});
			} else {
				setHintDesignModal({
					type: "simple",
					text: c.hintText || "",
					label: c.label || t("COMMON.HINT"),
				});
			}
		}
	}, [handleHintUsed, getModalSize]);

	// Juego parado / inválido en servidor: limpiar sesión (evita 500 en bucle con estado colgado)
	useEffect(() => {
		if (!group?._id) return;
		if (!game) return;
		if (game.active) return;
		resetPlaySessionToEnterCode(dispatch, navigate);
	}, [group?._id, game, dispatch, navigate]);

	// Cargar juego con escape room si hace falta (p. ej. al reconectar)
	useEffect(() => {
		const idStr =
			game?._id != null && String(game._id).trim() !== "" ? String(game._id) : groupGameIdStr;
		if (!idStr || idStr === "undefined") return;
		if (game?.escapeRoom) return;
		getGameById(idStr)
			.then((res) => {
				if (res.status !== 200 || !res.data) {
					resetPlaySessionToEnterCode(dispatch, navigate);
					return;
				}
				const g = res.data;
				if (!g.active) {
					resetPlaySessionToEnterCode(dispatch, navigate);
					return;
				}
				dispatch(setGame(g));
			})
			.catch(() => {
				resetPlaySessionToEnterCode(dispatch, navigate);
			});
	}, [game?._id, game?.escapeRoom, groupGameIdStr, dispatch, navigate]);

	// Redirigir si no hay escape room
	useEffect(() => {
		if (game && !game.escapeRoom && group?.lastVisited !== ROUTES.ESCAPE_ROOM) {
			navigate(ROUTES.START_GAME);
		}
	}, [game, group?.lastVisited, navigate]);

	if (!escapeRoom || playablePages.length === 0) {
		return (
			<div style={{ position: "fixed", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", background: "#0f172a", color: "#fff" }}>
				<div>Este juego no tiene escape room configurado o no hay páginas disponibles.</div>
			</div>
		);
	}

	const bgColor = currentPage?.backgroundColor || "#fff";
	const bgImgUrl = currentPage?.backgroundImageUrl;
	const bgImgResolved = bgImgUrl ? resolveEscapeRoomMediaUrl(bgImgUrl) : null;
	const onRegPage = isRegistrationPage(currentPage);
	const pendingRegisterRedirect = onRegPage && !isRegisteredGroup;
	const regBgResolved =
		onRegPage && currentPage?.registrationDesign?.backgroundImageUrl
			? resolveEscapeRoomMediaUrl(currentPage.registrationDesign.backgroundImageUrl)
			: null;

	const effectiveTransitionType = normalizePageTransitionForPlay(transitionType);
	const isComicPageTransition = effectiveTransitionType === "comic";
	const isScatterPageTransition = effectiveTransitionType === "scatter";
	const isRevealPageTransition = effectiveTransitionType === "reveal";
	const isStaggerEffectPageTransition = isStaggerEffectTransition(effectiveTransitionType);
	const pageStagger = isComicPageTransition
		? buildComicStaggerIndexMap(elements)
		: isScatterPageTransition
			? buildScatterStaggerIndexMap(elements)
			: isRevealPageTransition
				? buildRevealStaggerIndexMap(elements)
				: isStaggerEffectPageTransition
					? buildStaggerEffectIndexMap(elements)
					: null;
	const staggerBgStyle = isComicPageTransition
		? getComicPageBackgroundStyle(transitionDuration || 550)
		: isScatterPageTransition
			? getScatterPageBackgroundStyle(transitionDuration || 550)
			: isRevealPageTransition
				? getRevealPageBackgroundStyle(transitionDuration || 550)
				: isStaggerEffectPageTransition
					? getStaggerEffectPageBackgroundStyle(effectiveTransitionType, transitionDuration || 550)
					: null;

	const isMobilePlay = playViewport === "mobile";
	const playHudAccent = parseCssColor(game?.primaryColor) || "#7c3aed";

	if (pendingRegisterRedirect) {
		return (
			<div
				style={{
					position: "fixed",
					inset: 0,
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					background: "#0f172a",
					color: "#94a3b8",
					fontSize: 15,
					zIndex: 1,
				}}
			>
				{t("PLAY.LOADING_REGISTER")}
			</div>
		);
	}

	return (
		<div
			ref={playRootRef}
			style={{
				position: "fixed",
				inset: 0,
				width: "100%",
				minHeight: "100dvh",
				height: "100dvh",
				maxHeight: "100dvh",
				display: "flex",
				flexDirection: "column",
				background: "#0f172a",
				overflow: "hidden",
				zIndex: 1,
				boxSizing: "border-box",
				// Escritorio: sin insets para ocupar todo el viewport. Móvil: sin padding global (safe areas en HUD/footer).
				paddingLeft: 0,
				paddingRight: 0,
				paddingTop: 0,
				paddingBottom: 0,
			}}
		>
			<AmbientMusicPlayer
				url={escapeRoom?.ambientMusicUrl}
				loop={escapeRoom?.ambientMusicLoop !== false}
				volume={escapeRoom?.ambientMusicVolume}
			/>
			<style>{`
				@keyframes er-page-fade { from { opacity: 0; } to { opacity: 1; } }
				@keyframes er-page-slideLeft { from { opacity: 0; transform: translateX(20%); } to { opacity: 1; transform: translateX(0); } }
				@keyframes er-page-slideRight { from { opacity: 0; transform: translateX(-20%); } to { opacity: 1; transform: translateX(0); } }
				@keyframes er-page-slideUp { from { opacity: 0; transform: translateY(20%); } to { opacity: 1; transform: translateY(0); } }
				@keyframes er-page-slideDown { from { opacity: 0; transform: translateY(-20%); } to { opacity: 1; transform: translateY(0); } }
				@keyframes er-page-zoom { from { opacity: 0; transform: scale(0.92); } to { opacity: 1; transform: scale(1); } }
				@keyframes er-page-glitch-content {
					0% { opacity: 0.92; transform: translate(-5px, 2px) skewX(-0.4deg); filter: drop-shadow(5px 0 #ff00c8) drop-shadow(-5px 0 #00e5ff) contrast(1.15) saturate(1.2); }
					12% { opacity: 1; transform: translate(6px, -3px) skewX(0.3deg); filter: drop-shadow(-6px 2px #ff00c8) drop-shadow(5px -2px #00e5ff) contrast(1.25); }
					28% { transform: translate(-4px, 3px); filter: drop-shadow(4px -3px #ff00c8) drop-shadow(-4px 3px #00e5ff) contrast(1.2); }
					44% { transform: translate(3px, -1px); filter: drop-shadow(-3px 0 #ff00c8) drop-shadow(3px 0 #00e5ff); }
					60% { transform: translate(-2px, 1px); filter: drop-shadow(2px 1px #ff00c8) drop-shadow(-2px -1px #00e5ff); }
					100% { opacity: 1; transform: translate(0) skewX(0); filter: none; }
				}
				@keyframes er-page-comic-panel {
					0% { opacity: 0; transform: scale(0.18) rotate(var(--er-comic-rot, -6deg)); }
					42% { opacity: 1; transform: scale(1.07) rotate(calc(var(--er-comic-rot, -6deg) * 0.22)); }
					62% { transform: scale(0.97) rotate(calc(var(--er-comic-rot, -6deg) * 0.06)); }
					100% { opacity: 1; transform: scale(1) rotate(0deg); }
				}
				@keyframes er-page-comic-bg {
					0% { opacity: 0; transform: scale(1.04); }
					100% { opacity: 1; transform: scale(1); }
				}
				@keyframes er-page-scatter-panel {
					0% { opacity: 0; transform: translate(var(--er-scatter-x, 0), var(--er-scatter-y, 48px)) scale(0.88); filter: blur(8px); }
					55% { opacity: 1; filter: blur(1px); }
					100% { opacity: 1; transform: translate(0, 0) scale(1); filter: none; }
				}
				@keyframes er-page-scatter-bg {
					from { opacity: 0; }
					to { opacity: 1; }
				}
				@keyframes er-page-reveal-panel {
					0% {
						opacity: 0;
						transform: translateY(var(--er-reveal-drop, -140px)) rotate(var(--er-reveal-tilt, -7deg)) scale(0.88);
						filter: drop-shadow(0 0 0 rgba(0, 0, 0, 0));
					}
					20% { opacity: 1; }
					70% {
						transform: translateY(7px) rotate(calc(var(--er-reveal-tilt, -7deg) * 0.12)) scale(1.035);
						filter: drop-shadow(0 16px 14px rgba(0, 0, 0, 0.32));
					}
					84% {
						transform: translateY(-5px) rotate(calc(var(--er-reveal-tilt, -7deg) * 0.04)) scale(0.985);
						filter: drop-shadow(0 8px 10px rgba(0, 0, 0, 0.18));
					}
					100% {
						opacity: 1;
						transform: translateY(0) rotate(0deg) scale(1);
						filter: drop-shadow(0 3px 7px rgba(0, 0, 0, 0.1));
					}
				}
				@keyframes er-page-reveal-bg {
					0% { opacity: 0; filter: brightness(0.5) saturate(0.88); }
					100% { opacity: 1; filter: none; }
				}
				@keyframes er-entry-scatter {
					from { opacity: 0; transform: translateY(20px) scale(0.9); filter: blur(5px); }
					to { opacity: 1; transform: translateY(0) scale(1); filter: none; }
				}
				@keyframes er-entry-comic {
					0% { opacity: 0; transform: scale(1.28) rotate(-3.5deg); filter: contrast(1.28) saturate(1.3); }
					32% { opacity: 1; transform: scale(0.94) rotate(1.8deg); }
					58% { transform: scale(1.035) rotate(-0.6deg); }
					100% { opacity: 1; transform: scale(1) rotate(0); filter: none; }
				}
				@keyframes er-page-materialize-panel {
					0% {
						opacity: 0;
						filter: blur(var(--er-mat-blur, 14px)) saturate(0.12) brightness(1.85) contrast(0.55)
							drop-shadow(0 0 calc(18px * var(--er-mat-glow, 0.6)) rgba(186, 220, 255, var(--er-mat-glow, 0.6)));
					}
					28% {
						opacity: 0.5;
						filter: blur(calc(var(--er-mat-blur, 14px) * 0.45)) saturate(0.4) brightness(1.45) contrast(0.78)
							drop-shadow(0 0 calc(12px * var(--er-mat-glow, 0.6)) rgba(200, 225, 255, calc(var(--er-mat-glow, 0.6) * 0.7)));
					}
					62% {
						opacity: 0.9;
						filter: blur(2px) saturate(0.82) brightness(1.06) contrast(1);
					}
					100% { opacity: 1; filter: none; }
				}
				@keyframes er-page-materialize-bg {
					0% { opacity: 0; filter: blur(4px) saturate(0.35) brightness(0.65); }
					100% { opacity: 1; filter: none; }
				}
				@keyframes er-entry-materialize {
					0% { opacity: 0; filter: blur(12px) saturate(0.15) brightness(1.7); }
					100% { opacity: 1; filter: none; }
				}
				@keyframes er-page-build-panel {
					0% {
						opacity: 0;
						transform: translate3d(var(--er-build-dx, 0px), var(--er-build-dy, 16px), 0) scale(1.015);
						filter: drop-shadow(0 12px 20px rgba(0, 0, 0, 0.30));
					}
					62% {
						opacity: 1;
						transform: translate3d(0, 0, 0) scale(1);
						filter: drop-shadow(0 4px 8px rgba(0, 0, 0, 0.16));
					}
					86% {
						transform: translate3d(0, -1px, 0) scale(1);
						filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.10));
					}
					100% {
						opacity: 1;
						transform: none;
						filter: none;
					}
				}
				@keyframes er-page-build-bg {
					0% {
						opacity: 0;
						filter: brightness(0.88) contrast(1.04);
					}
					100% { opacity: 1; filter: none; }
				}
				@keyframes er-entry-build {
					0% {
						opacity: 0;
						transform: translate3d(0, 16px, 0) scale(1.015);
						filter: drop-shadow(0 12px 20px rgba(0, 0, 0, 0.28));
					}
					62% {
						opacity: 1;
						transform: translate3d(0, 0, 0) scale(1);
						filter: drop-shadow(0 4px 8px rgba(0, 0, 0, 0.14));
					}
					86% {
						transform: translate3d(0, -1px, 0);
						filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.08));
					}
					100% {
						opacity: 1;
						transform: none;
						filter: none;
					}
				}
				@media (prefers-reduced-motion: reduce) {
					@keyframes er-page-build-panel {
						0% { opacity: 0; }
						100% { opacity: 1; transform: none; filter: none; }
					}
					@keyframes er-entry-build {
						0% { opacity: 0; }
						100% { opacity: 1; transform: none; filter: none; }
					}
				}
				@keyframes er-page-static-panel {
					0% { opacity: 0.12; filter: contrast(4) brightness(2.2) blur(2px) saturate(0); }
					18% { opacity: 0.62; filter: contrast(3) brightness(1.85) blur(1.5px); }
					36% { opacity: 0.32; filter: contrast(2.6) brightness(2) blur(1px); }
					54% { opacity: 0.78; filter: contrast(1.7) brightness(1.35) blur(0.5px); }
					72% { opacity: 0.52; filter: contrast(1.25) brightness(1.12); }
					100% { opacity: 1; filter: none; }
				}
				@keyframes er-page-static-bg {
					0% { opacity: 0; filter: contrast(2.2) brightness(1.5) saturate(0); }
					100% { opacity: 1; filter: none; }
				}
				@keyframes er-entry-static {
					0%, 35% { opacity: 0.35; filter: contrast(3) brightness(1.8) blur(1px); }
					100% { opacity: 1; filter: none; }
				}
				@keyframes er-page-thermal-panel {
					0% { opacity: 0; transform: scale(0.88); filter: blur(7px) brightness(2.4) hue-rotate(var(--er-neon-hue, 0deg)) drop-shadow(0 0 16px rgba(0, 255, 220, 0.95)) drop-shadow(0 0 32px rgba(255, 0, 200, 0.65)); }
					14% { opacity: 1; transform: scale(1.06); filter: blur(0) brightness(1.85) hue-rotate(var(--er-neon-hue, 0deg)) saturate(1.8) drop-shadow(0 0 20px rgba(0, 255, 220, 0.9)) drop-shadow(0 0 40px rgba(255, 0, 200, 0.5)); }
					32% { transform: scale(0.98); filter: brightness(1.3) hue-rotate(calc(var(--er-neon-hue, 0deg) * 0.35)) drop-shadow(0 0 10px rgba(0, 255, 220, 0.45)); }
					52% { transform: scale(1.01); filter: brightness(1.1) drop-shadow(0 0 5px rgba(0, 255, 220, 0.2)); }
					100% { opacity: 1; transform: none; filter: none; }
				}
				@keyframes er-page-thermal-bg {
					0% { opacity: 0; filter: brightness(0.35) saturate(1.5) contrast(1.25); }
					18% { opacity: 0.75; filter: brightness(1.35) saturate(1.7); }
					42% { opacity: 1; filter: brightness(1.1); }
					100% { opacity: 1; filter: none; }
				}
				@keyframes er-entry-thermal {
					0% { opacity: 0; transform: scale(0.9); filter: blur(6px) brightness(2) drop-shadow(0 0 14px rgba(0, 255, 220, 0.85)); }
					38% { opacity: 1; transform: scale(1.04); filter: brightness(1.45) drop-shadow(0 0 10px rgba(0, 255, 220, 0.5)); }
					100% { opacity: 1; transform: none; filter: none; }
				}
				@keyframes er-page-softGlitch-panel {
					0% { opacity: 0; transform: translate(var(--er-glitch-x, 9px), var(--er-glitch-y, -4px)) skewX(var(--er-glitch-skew, 5deg)) scale(1.08); filter: drop-shadow(calc(var(--er-glitch-x, 9px) * 1.3) 0 #ff00c8) drop-shadow(calc(var(--er-glitch-x, 9px) * -1.3) 0 #00e5ff) contrast(2.6) saturate(2) brightness(1.55); }
					10% { opacity: 1; transform: translate(calc(var(--er-glitch-x, 9px) * -0.9), calc(var(--er-glitch-y, -4px) * -0.5)) skewX(calc(var(--er-glitch-skew, 5deg) * -1.6)) scale(1.04); filter: drop-shadow(-8px 2px #ff00c8) drop-shadow(8px -2px #00e5ff) contrast(2.3) brightness(1.35); }
					20% { transform: translate(calc(var(--er-glitch-x, 9px) * 0.55), 3px) skewX(calc(var(--er-glitch-skew, 5deg) * 0.7)) scale(0.97); filter: drop-shadow(6px -1px #ff00c8) drop-shadow(-6px 1px #00e5ff) contrast(2.8) brightness(1.7); }
					30% { transform: translate(calc(var(--er-glitch-x, 9px) * -0.45), -2px) skewX(calc(var(--er-glitch-skew, 5deg) * -1.1)) scale(1.03); }
					40% { transform: translate(4px, 1px) skewX(2deg) scale(0.99); filter: contrast(2) drop-shadow(4px 0 rgba(255,0,200,0.7)) drop-shadow(-4px 0 rgba(0,229,255,0.7)); }
					50% { transform: translate(-3px, -1px) skewX(-2deg); filter: contrast(1.7); }
					62% { transform: translate(2px, 0) skewX(1deg) scale(1.01); filter: drop-shadow(2px 0 rgba(255,0,200,0.4)) drop-shadow(-2px 0 rgba(0,229,255,0.4)); }
					75% { transform: translate(-1px, 0) skewX(-0.5deg); filter: contrast(1.25); }
					88% { transform: translate(1px, 0); filter: contrast(1.08); }
					100% { opacity: 1; transform: none; filter: none; }
				}
				@keyframes er-page-softGlitch-bg {
					0% { opacity: 0; filter: contrast(2) saturate(0.6) brightness(1.3); }
					30% { opacity: 0.85; filter: contrast(1.5) saturate(0.85); }
					100% { opacity: 1; filter: none; }
				}
				@keyframes er-entry-softGlitch {
					0% { opacity: 0; transform: translate(8px, -3px) skewX(4deg); filter: drop-shadow(6px 0 #ff00c8) drop-shadow(-6px 0 #00e5ff) contrast(2.4); }
					50% { opacity: 1; transform: translate(-3px, 1px) skewX(-2deg); filter: drop-shadow(3px 0 rgba(255,0,200,0.6)) drop-shadow(-3px 0 rgba(0,229,255,0.6)); }
					100% { opacity: 1; transform: none; filter: none; }
				}
				@keyframes er-page-mist-content-in {
					0% { filter: blur(20px) brightness(0.58) saturate(0.3) contrast(0.92); }
					30% { filter: blur(11px) brightness(0.78) saturate(0.5) contrast(0.96); }
					58% { filter: blur(4px) brightness(0.94) saturate(0.82); }
					100% { filter: none; }
				}
				@keyframes er-page-mist-panel {
					0% { opacity: 0; transform: translateY(calc(14px + var(--er-mist-drift, 0px))) scale(0.97); filter: blur(calc(18px + var(--er-mist-blur, 0px))) saturate(0.22) brightness(0.92) contrast(0.9); }
					18% { opacity: 0.3; filter: blur(14px) saturate(0.28) brightness(0.95); }
					38% { opacity: 0.62; transform: translateY(calc(5px + var(--er-mist-drift, 0px) * 0.35)) scale(0.99); filter: blur(8px) saturate(0.45) brightness(0.98); }
					58% { opacity: 0.84; filter: blur(3px) saturate(0.68); }
					78% { opacity: 0.95; filter: blur(1px); }
					100% { opacity: 1; transform: none; filter: none; }
				}
				@keyframes er-page-mist-bg {
					0% { opacity: 0; filter: blur(16px) saturate(0.2) brightness(0.62) contrast(0.9); }
					28% { opacity: 0.55; filter: blur(9px) saturate(0.35) brightness(0.78); }
					55% { opacity: 0.82; filter: blur(4px) saturate(0.62) brightness(0.9); }
					100% { opacity: 1; filter: none; }
				}
				@keyframes er-entry-mist {
					0% { opacity: 0; transform: translateY(14px); filter: blur(14px) saturate(0.25) brightness(0.9); }
					45% { opacity: 0.78; filter: blur(4px) saturate(0.55); }
					100% { opacity: 1; transform: none; filter: none; }
				}
				@keyframes er-page-shadow-panel {
					0% { opacity: 0; transform: translateX(var(--er-shadow-x, 6px)) scale(1.15); filter: brightness(0.12) contrast(1.9) saturate(0.25) blur(4px); }
					18% { opacity: 0.28; transform: translateX(calc(var(--er-shadow-x, 6px) * 0.65)) scale(1.09); filter: brightness(0.32) contrast(1.7) blur(3px); }
					40% { opacity: 0.68; transform: translateX(calc(var(--er-shadow-x, 6px) * 0.25)) scale(1.03); filter: brightness(0.62) contrast(1.35) blur(1px); }
					65% { opacity: 0.88; transform: scale(0.99); filter: brightness(0.88) contrast(1.12); }
					100% { opacity: 1; transform: none; filter: none; }
				}
				@keyframes er-page-shadow-bg {
					0% { opacity: 0; filter: brightness(0.18) contrast(1.55) saturate(0.12); }
					42% { opacity: 0.78; filter: brightness(0.52) contrast(1.22); }
					100% { opacity: 1; filter: none; }
				}
				@keyframes er-entry-shadow {
					0% { opacity: 0; transform: scale(1.12); filter: brightness(0.18) contrast(1.75) blur(3px); }
					52% { opacity: 0.82; filter: brightness(0.72); }
					100% { opacity: 1; transform: none; filter: none; }
				}
				@keyframes er-page-omen-panel {
					0% { opacity: 0; transform: rotate(var(--er-omen-tilt, 2deg)) translateY(10px) scale(0.93); filter: blur(5px) brightness(1.65) saturate(0.35) hue-rotate(278deg) drop-shadow(0 0 14px rgba(76, 29, 149, 0.75)); }
					10% { opacity: 0.18; transform: rotate(calc(var(--er-omen-tilt, 2deg) * -0.9)) translateY(5px) scale(0.96); }
					20% { opacity: 0.88; filter: blur(2px) brightness(1.35) hue-rotate(265deg) drop-shadow(0 0 10px rgba(88, 28, 135, 0.55)); }
					30% { opacity: 0.38; transform: rotate(calc(var(--er-omen-tilt, 2deg) * 0.55)) scale(1.02); }
					40% { opacity: 0.96; filter: brightness(1.12) hue-rotate(252deg); }
					50% { opacity: 0.48; }
					60% { opacity: 1; transform: rotate(calc(var(--er-omen-tilt, 2deg) * -0.25)) scale(0.99); filter: brightness(1.04); }
					72% { opacity: 0.7; }
					82% { opacity: 1; }
					100% { opacity: 1; transform: none; filter: none; }
				}
				@keyframes er-page-omen-bg {
					0% { opacity: 0; filter: brightness(0.35) saturate(0.3) hue-rotate(285deg) contrast(1.35); }
					28% { opacity: 0.55; filter: brightness(0.65) hue-rotate(272deg); }
					100% { opacity: 1; filter: none; }
				}
				@keyframes er-entry-omen {
					0% { opacity: 0; transform: rotate(2.5deg) scale(0.94); filter: brightness(1.55) hue-rotate(280deg) drop-shadow(0 0 12px rgba(76, 29, 149, 0.65)); }
					45% { opacity: 0.65; }
					100% { opacity: 1; transform: none; filter: none; }
				}
				@keyframes er-page-clinical-panel {
					0% {
						opacity: 0;
						transform: translateY(var(--er-clinical-rise, 8px)) skewX(var(--er-clinical-skew, 0.5deg)) scale(0.96);
						filter: blur(7px) brightness(1.35) saturate(0.45) hue-rotate(-22deg)
							drop-shadow(0 0 calc(14px * var(--er-clinical-glow, 0.5)) rgba(16, 185, 129, calc(0.55 + var(--er-clinical-glow, 0.5) * 0.4)));
					}
					18% {
						opacity: 0.55;
						filter: blur(3px) brightness(1.18) saturate(0.75) hue-rotate(-12deg)
							drop-shadow(0 0 10px rgba(16, 185, 129, 0.45));
					}
					36% {
						opacity: 1;
						transform: translateY(0) skewX(0deg) scale(1.035);
						filter: brightness(1.1) saturate(1.05)
							drop-shadow(0 0 16px rgba(203, 255, 151, 0.55));
					}
					52% {
						transform: scale(0.985);
						filter: brightness(1.02) drop-shadow(0 0 6px rgba(16, 185, 129, 0.25));
					}
					70% {
						transform: scale(1.012);
						filter: brightness(1.04) drop-shadow(0 0 8px rgba(203, 255, 151, 0.3));
					}
					100% { opacity: 1; transform: none; filter: none; }
				}
				@keyframes er-page-clinical-bg {
					0% { opacity: 0; filter: brightness(0.4) saturate(1.2) hue-rotate(-20deg) contrast(1.15); }
					22% { opacity: 0.55; filter: brightness(0.72) saturate(1.1) hue-rotate(-10deg); }
					55% { opacity: 0.92; filter: brightness(0.95) saturate(1.05); }
					100% { opacity: 1; filter: none; }
				}
				@keyframes er-entry-clinical {
					0% {
						opacity: 0;
						transform: translateY(10px) scale(0.96);
						filter: blur(6px) brightness(1.3) hue-rotate(-18deg) drop-shadow(0 0 12px rgba(16, 185, 129, 0.55));
					}
					38% {
						opacity: 1;
						transform: scale(1.03);
						filter: brightness(1.08) drop-shadow(0 0 10px rgba(203, 255, 151, 0.4));
					}
					100% { opacity: 1; transform: none; filter: none; }
				}
				@keyframes er-entry-reveal {
					0% { opacity: 0; transform: translateY(-52px) rotate(-6deg) scale(0.9); filter: drop-shadow(0 0 0 rgba(0,0,0,0)); }
					72% { transform: translateY(5px) rotate(1.5deg) scale(1.03); filter: drop-shadow(0 10px 8px rgba(0,0,0,0.22)); }
					100% { opacity: 1; transform: translateY(0) rotate(0) scale(1); filter: drop-shadow(0 2px 5px rgba(0,0,0,0.1)); }
				}
				@keyframes er-entry-fade { from { opacity: 0; } to { opacity: 1; } }
				@keyframes er-entry-slideLeft { from { opacity: 0; transform: translateX(16%); } to { opacity: 1; transform: translateX(0); } }
				@keyframes er-entry-slideRight { from { opacity: 0; transform: translateX(-16%); } to { opacity: 1; transform: translateX(0); } }
				@keyframes er-entry-slideUp { from { opacity: 0; transform: translateY(16%); } to { opacity: 1; transform: translateY(0); } }
				@keyframes er-entry-slideDown { from { opacity: 0; transform: translateY(-16%); } to { opacity: 1; transform: translateY(0); } }
				@keyframes er-entry-zoom { from { opacity: 0; transform: scale(0.88); } to { opacity: 1; transform: scale(1); } }
				@keyframes er-entry-glitch {
					0% { opacity: 0; transform: translate(-2px, 1px); filter: drop-shadow(3px 0 #ff00c8) drop-shadow(-3px 0 #00e5ff); }
					22% { opacity: 1; transform: translate(3px, -2px); filter: drop-shadow(-4px 1px #ff00c8) drop-shadow(4px -1px #00e5ff); }
					44% { transform: translate(-2px, 2px); filter: drop-shadow(2px -2px #ff00c8) drop-shadow(-2px 2px #00e5ff); }
					66% { transform: translate(1px, 0); filter: drop-shadow(-1px 0 #ff00c8) drop-shadow(1px 0 #00e5ff); }
					100% { opacity: 1; transform: translate(0); filter: none; }
				}
				@keyframes er-feedback-fadeIn { from { opacity: 0; } to { opacity: 1; } }
				@keyframes er-feedback-popCorrect { from { opacity: 0; transform: scale(0.5); } to { opacity: 1; transform: scale(1); } }
				@keyframes er-feedback-popWrong { from { opacity: 0; transform: scale(0.5) rotate(-3deg); } to { opacity: 1; transform: scale(1) rotate(0deg); } }
				@keyframes er-feedback-iconBounce { 0% { transform: scale(0); opacity: 0; } 50% { transform: scale(1.15); } 70% { transform: scale(0.95); } 100% { transform: scale(1); opacity: 1; } }
				@keyframes er-modal-overlay-in { from { opacity: 0; } to { opacity: 1; } }
				@keyframes er-modal-content-in { from { opacity: 0; transform: scale(0.94); } to { opacity: 1; transform: scale(1); } }
			`}</style>
			<div
				ref={containerRef}
				style={{
					position: "absolute",
					inset: 0,
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					overflow: "hidden",
				}}
			>
				<div
					key={transitionKey}
					style={{
						position: "relative",
						...getPageTransitionWrapperStyle(effectiveTransitionType, transitionDuration),
					}}
				>
					<div
						className="escape-room-canvas"
						style={{
							position: "relative",
							width: playDim.width,
							height: playDim.height,
							...escapeRoomEditorBackgroundSurfaceStyle(
								onRegPage ? (currentPage?.registrationDesign?.backgroundColor || "#fff") : bgColor,
								"#fff"
							),
							boxShadow: "none",
							borderRadius: 0,
							overflow: "hidden",
							transform: `scale(${scale})`,
							transformOrigin: "center center",
							userSelect: "none",
						}}
					>
					{regBgResolved ? (
						<div style={{ position: "absolute", inset: 0, zIndex: 0 }}>
							<EscapeRoomBackgroundMediaLayer mediaUrl={regBgResolved} opacity={100} zIndex={0} />
						</div>
					) : null}
					{!onRegPage && !bgImgResolved && staggerBgStyle ? (
						<div
							style={{
								position: "absolute",
								inset: 0,
								zIndex: 0,
								pointerEvents: "none",
								...staggerBgStyle,
							}}
							aria-hidden
						/>
					) : null}
					{!onRegPage && bgImgResolved ? (
						<div style={{ position: "absolute", inset: 0, zIndex: 0 }}>
							<EscapeRoomBackgroundMediaLayer
								mediaUrl={bgImgResolved}
								opacity={currentPage?.backgroundImageOpacity != null ? currentPage.backgroundImageOpacity : 100}
								zIndex={0}
								videoLoop={!currentPage?.advanceOnBackgroundVideoEnd}
								onVideoEnded={currentPage?.advanceOnBackgroundVideoEnd ? handleBackgroundVideoEnded : undefined}
								onVideoError={currentPage?.advanceOnBackgroundVideoEnd ? handleBackgroundVideoError : undefined}
								videoPlaybackRate={currentPage?.backgroundVideoPlaybackRate ?? 1}
							/>
						</div>
					) : null}
					<div style={{ position: "relative", zIndex: 1, width: "100%", height: "100%" }}>
					{(() => {
						const quizIds = (elements || []).filter((e) => e.type === "quiz").map((e) => e.elementId);
						const allQuizzesOnPageCompleted = quizIds.length === 0 || quizIds.every((id) => {
							const a = answers[id];
							return !!(a && a.done);
						});
						const activatedSet = activatedButtonsByPage[String(pageIndex)] || new Set();
						const pageButtons = (elements || []).filter((e) => e.type === "button");
						const pageAnimKey = `${transitionKey}-${pageIndex}`;
						return elements.map((el) => {
							let buttonVisible = true;
							let buttonDisabled = false;
							const showWhenBtn = el.type === "button" ? (el.content?.showWhen || "always") : "";
							const revealedAfter =
								showWhenBtn === "afterSeconds" && afterSecondsReady.has(String(el.elementId));
							const elAnimKey =
								el.type === "button" && showWhenBtn === "afterSeconds"
									? `${pageAnimKey}${revealedAfter ? "-shown" : "-wait"}`
									: pageAnimKey;
							const staggerIdx = pageStagger?.map.get(String(el.elementId));
							const staggerCtx = { showWhenBtn, revealedAfter, buttonVisible };
							let activeStaggerType;
							if (staggerIdx != null) {
								if (isComicPageTransition && shouldApplyComicPageElementTransition(staggerCtx)) {
									activeStaggerType = "comic";
								} else if (isScatterPageTransition && shouldApplyScatterPageElementTransition(staggerCtx)) {
									activeStaggerType = "scatter";
								} else if (isRevealPageTransition && shouldApplyRevealPageElementTransition(staggerCtx)) {
									activeStaggerType = "reveal";
								} else if (
									isStaggerEffectPageTransition
									&& shouldApplyStaggerEffectPageElementTransition(effectiveTransitionType, staggerCtx)
								) {
									activeStaggerType = effectiveTransitionType;
								}
							}
							const entryAnimationStyle = getElementEntryAnimationStyle(el, elAnimKey, {
								pageTransitionType: activeStaggerType,
								staggerIndex: activeStaggerType ? staggerIdx : undefined,
								staggerTotal: pageStagger?.total,
								pageTransitionDurationMs: transitionDuration || 550,
							});
							if (el.type === "button") {
								const showWhen = showWhenBtn;
								if (showWhen === "quizCompleted") {
									buttonVisible = allQuizzesOnPageCompleted;
								} else if (showWhen === "allButtonsActivated") {
									const otherButtons = pageButtons.filter((b) => b.elementId !== el.elementId);
									buttonVisible = otherButtons.length === 0 || otherButtons.every((b) => activatedSet.has(b.elementId));
								} else if (showWhen === "afterSeconds") {
									buttonVisible = afterSecondsReady.has(String(el.elementId));
								}
								if (buttonVisible && hideAfterExpired.has(String(el.elementId))) {
									buttonVisible = false;
								}
								buttonDisabled = !!el.content?.onlyOnce && activatedSet.has(el.elementId);
							}
							return (
								<EscapeRoomElement
									key={`${el.elementId}-${elAnimKey}`}
									element={el}
									entryAnimationStyle={entryAnimationStyle}
									animKey={pageAnimKey}
									quizData={el.type === "quiz" ? quizzes[resolveQuizId(el.content?.quizId)] : undefined}
									answer={answers[el.elementId]}
									registrationDesign={currentPage?.registrationDesign}
									onButtonAction={handleButtonAction}
									onQuizOptionSelect={handleQuizOptionSelect}
									onQuizCustomAnswer={handleQuizCustomAnswer}
									onHintUsed={handleHintUsed}
									onHintDesignOpen={handleHintDesignOpen}
									allQuizzesOnPageCompleted={allQuizzesOnPageCompleted}
									buttonVisible={el.type === "button" ? buttonVisible : undefined}
									buttonDisabled={el.type === "button" ? buttonDisabled : undefined}
									playerScore={group?.score ?? 0}
								/>
							);
						});
					})()}
					</div>
					{effectiveTransitionType === "glitch" ? (
						<EscapeRoomPageGlitchOverlay
							durationMs={transitionDuration || 350}
							overlayKey={`${transitionKey}-glitch`}
						/>
					) : null}
					{effectiveTransitionType === "mist" ? (
						<EscapeRoomPageMistOverlay
							durationMs={transitionDuration || 550}
							overlayKey={`${transitionKey}-mist`}
						/>
					) : null}
					{effectiveTransitionType === "build" ? (
						<EscapeRoomPageBlueprintOverlay
							layoutItems={(pageStagger?.sorted || []).slice(0, 8).map((el) => ({
								top: el.y,
								left: el.x,
								width: el.width,
								height: el.height,
							}))}
							staggerMs={90}
							overlayKey={`${transitionKey}-build`}
						/>
					) : null}
					{isFlashEnterTransition(effectiveTransitionType) ? (
						<EscapeRoomPageFlashOverlay
							mode={effectiveTransitionType === "flashBoth" ? "both" : "enter"}
							durationMs={transitionDuration || 350}
							overlayKey={`${transitionKey}-flash-enter`}
						/>
					) : null}
					{exitFlash ? (
						<EscapeRoomPageFlashOverlay
							mode="exit"
							durationMs={exitFlash.durationMs || 350}
							overlayKey={`exit-${exitFlash.key}`}
						/>
					) : null}
					</div>
				</div>
			</div>
			<div
				style={{
					position: "absolute",
					top: 0,
					left: 0,
					right: 0,
					zIndex: 10,
					marginTop: isMobilePlay ? "env(safe-area-inset-top, 0px)" : 0,
					paddingTop: isMobilePlay ? 4 : "max(10px, env(safe-area-inset-top, 0px))",
					paddingBottom: isMobilePlay ? 4 : 10,
					paddingLeft: isMobilePlay
						? "max(8px, env(safe-area-inset-left, 0px))"
						: "max(12px, env(safe-area-inset-left, 0px))",
					paddingRight: isMobilePlay
						? "max(8px, env(safe-area-inset-right, 0px))"
						: "max(12px, env(safe-area-inset-right, 0px))",
					display: "flex",
					justifyContent: isMobilePlay
						? "flex-end"
						: showPlayerScore
							? "space-between"
							: "center",
					alignItems: "center",
					gap: isMobilePlay ? 6 : 10,
					pointerEvents: "none",
					background: isMobilePlay
						? "linear-gradient(180deg, rgba(15,23,42,0.28) 0%, rgba(15,23,42,0) 85%)"
						: "linear-gradient(180deg, rgba(15,23,42,0.42) 0%, rgba(15,23,42,0) 100%)",
					fontFamily: '"League Spartan", system-ui, -apple-system, sans-serif',
				}}
			>
				<div
					aria-label={t("PLAY.TIME_LEFT")}
					style={{
						pointerEvents: "auto",
						display: "inline-flex",
						alignItems: "center",
						justifyContent: "center",
						gap: 2,
						minWidth: isMobilePlay ? 44 : 52,
						padding: isMobilePlay ? "5px 10px" : "7px 14px",
						borderRadius: 999,
						background: "rgba(255,255,255,0.94)",
						boxShadow: "0 2px 14px rgba(15,23,42,0.1), 0 0 0 1px rgba(15,23,42,0.06)",
						color: playHudAccent,
						fontWeight: 800,
						fontSize: isMobilePlay ? 13 : 15,
						letterSpacing: "-0.03em",
						fontVariantNumeric: "tabular-nums",
						lineHeight: 1.1,
					}}
				>
					<span ref={timerMinElRef}>{formatMinutes(playHudTimeRef.current, gameTimeoutSeconds, group?.finished)}</span>
					<span style={{ opacity: 0.85, padding: "0 1px" }}>:</span>
					<span ref={timerSecElRef}>{formatSeconds(playHudTimeRef.current, gameTimeoutSeconds, group?.finished)}</span>
				</div>
				{showPlayerScore ? (
				<div
					aria-label={t("PLAY.POINTS", { score: group?.score ?? 0 })}
					style={{
						pointerEvents: "auto",
						display: "inline-flex",
						alignItems: "center",
						justifyContent: "center",
						minWidth: isMobilePlay ? 34 : 48,
						padding: isMobilePlay ? "5px 10px" : "7px 16px",
						borderRadius: 999,
						background: "rgba(255,255,255,0.94)",
						boxShadow: "0 2px 14px rgba(15,23,42,0.1), 0 0 0 1px rgba(15,23,42,0.06)",
						color: playHudAccent,
						fontWeight: 800,
						fontSize: isMobilePlay ? 14 : 16,
						letterSpacing: "-0.04em",
						fontVariantNumeric: "tabular-nums",
						lineHeight: 1.1,
					}}
				>
					{group?.score ?? 0}
				</div>
				) : null}
			</div>
			{currentPage?.showFooter && Array.isArray(escapeRoom?.scenarios) && escapeRoom.scenarios.length > 0 && (() => {
				const sorted = (escapeRoom.scenarios || []).slice().sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
				const currentScId = (currentPage?.scenarioId || "").trim();
				const scenarioPassed = (sc) => {
					const idxs = playablePages.map((p, i) => ((p?.scenarioId || "") === sc.id ? i : -1)).filter((i) => i >= 0);
					return idxs.length > 0 && pageIndex > Math.max(...idxs);
				};
				const visible = sorted.filter((s) => !suppressedScenarioIds.has(s.id));
				const branchGroups = computeExclusiveBranchGroups(playablePages);
				const visitedScenarios = scenariosVisitedUpToPage(playablePages, pageIndex);
				const stations = [];
				let vi = 0;
				while (vi < visible.length) {
					const sc = visible[vi];
					const scN = normScId(sc.id);
					const group = branchGroups.find((g) => g.has(scN));
					const enteredAnyInGroup = group && [...group].some((id) => visitedScenarios.has(id));
					const inVisible = group ? [...group].filter((id) => visible.some((v) => normScId(v.id) === id)) : [];
					if (group && !enteredAnyInGroup && inVisible.length > 1 && group.has(scN)) {
						stations.push({ kind: "branch", ids: inVisible });
						while (vi < visible.length && group.has(normScId(visible[vi].id))) vi++;
						continue;
					}
					stations.push({ kind: "scene", sc });
					vi++;
				}
				const accentColors = ["#FF6017", "#8c3888", "#69D0FF", "#53CE76"];
				return (
					<div className={`scenarioBar ${footerOpen ? "active" : "inactive"}`} style={{ position: "absolute" }}>
						<div
							className="buttonDown"
							onClick={() => setFooterOpen((v) => !v)}
							title={footerOpen ? t("PLAY.COLLAPSE") : t("PLAY.EXPAND")}
							style={{ right: 12 }}
						>
							<span style={{ color: "#fff", fontWeight: 800, fontSize: 20, marginTop: -10 }}>{footerOpen ? "▾" : "▴"}</span>
						</div>
						<div className="er-stations-wrap">
							<div className="er-stations-track">
								{stations.map((st, idx) => {
									const prev = idx > 0 ? stations[idx - 1] : null;
									const railPassed = prev?.kind === "scene" && scenarioPassed(prev.sc);
									const key = st.kind === "branch" ? `branch-${st.ids.join("-")}` : st.sc.id;
									return (
										<React.Fragment key={key}>
											{idx > 0 && <div className={`er-station-rail-mid${railPassed ? " er-station-rail-mid--passed" : ""}`} aria-hidden />}
											{st.kind === "branch" ? (
												<div className="er-station-cell er-station-cell--branch">
													<div className="er-station-node er-station-node--branch">
														<span className="er-station-q">?</span>
													</div>
													<div className="er-station-label">
														<div className="er-station-title">{t("PLAY.UNDISCOVERED")}</div>
														<div className="er-station-sub">{t("PLAY.BRANCHES")}</div>
													</div>
												</div>
											) : (() => {
												const sc = st.sc;
												const sidx = sorted.findIndex((s) => s.id === sc.id);
												const isCurrent = currentScId === sc.id;
												const passed = scenarioPassed(sc);
												const IconComp = getButtonIconComponent(sc.icon) || getButtonIconComponent("flag");
												const accent = accentColors[(sidx >= 0 ? sidx : 0) % accentColors.length] || "#7D00FC";
												return (
													<div className={`er-station-cell${isCurrent ? " er-station-cell--current" : ""}${passed ? " er-station-cell--passed" : ""}`}>
														<div
															className="er-station-node"
															style={
																isCurrent
																	? {
																		boxShadow: "0 0 0 4px rgba(255,255,255,0.35), 0 10px 24px rgba(0,0,0,0.25)",
																		borderColor: "#fff",
																		background: accent,
																	}
																	: passed
																		? { background: accent, borderColor: "rgba(255,255,255,0.85)" }
																		: undefined
															}
														>
															<IconComp size={20} stroke={2} style={{ color: "#fff" }} />
														</div>
														<div className="er-station-label">
															<div className="er-station-title">{sc.name || t("COMMON.SCENARIO_N", { n: sidx + 1 })}</div>
															<div className="er-station-sub">{isCurrent ? t("PLAY.NOW") : passed ? t("PLAY.DONE") : t("PLAY.PENDING")}</div>
														</div>
													</div>
												);
											})()}
										</React.Fragment>
									);
								})}
							</div>
						</div>
					</div>
				);
			})()}
			{loading && (
				<div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.3)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9998 }}>
					<div style={{ color: "#fff", fontSize: 18 }}>{t("PLAY.SAVING")}</div>
				</div>
			)}
			{quizFeedbackModal && (
				<QuizFeedbackModal
					modal={quizFeedbackModal}
					onClose={() => setQuizFeedbackModal(null)}
					overlayZIndex={modalContent || hintDesignModal ? 10050 : 9999}
				/>
			)}
			{hintDesignModal && (
				<div
					style={{
						position: "fixed",
						inset: 0,
						zIndex: 10000,
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						background: "rgba(0,0,0,0.6)",
						padding: 16,
						boxSizing: "border-box",
						animation: "er-modal-overlay-in 0.22s ease-out both",
					}}
					onClick={(e) => e.target === e.currentTarget && (hintDesignModal.type === "hint" || hintDesignModal.type === "simple") && setHintDesignModal(null)}
				>
					{hintDesignModal.type === "simple" ? (
						<div
							style={{
								background: "#fff",
								padding: 24,
								borderRadius: 12,
								maxWidth: 400,
								maxHeight: "80vh",
								overflow: "auto",
								boxShadow: "0 12px 48px rgba(0,0,0,0.4)",
								animation: "er-modal-content-in 0.28s cubic-bezier(0.34, 1.2, 0.64, 1) both",
							}}
							onClick={(e) => e.stopPropagation()}
						>
							<h3 style={{ margin: "0 0 16px", fontSize: 20 }}>{hintDesignModal.label}</h3>
							<div style={{ whiteSpace: "pre-wrap", marginBottom: 16 }}>{hintDesignModal.text}</div>
							<button
								onClick={() => setHintDesignModal(null)}
								style={{ padding: "10px 20px", cursor: "pointer", borderRadius: 8, border: "none", background: "#7c3aed", color: "#fff", fontWeight: "bold" }}
							>
								{t("COMMON.CLOSE")}
							</button>
						</div>
					) : (
						<div
							style={{
								position: "relative",
								width: hintDesignModal.size.width,
								height: hintDesignModal.size.height,
								minWidth: 200,
								minHeight: 150,
								...escapeRoomEditorBackgroundSurfaceStyle(hintDesignModal.design?.backgroundColor || "#ffffff"),
								borderRadius: hintDesignModal.design?.borderRadius ?? 12,
								boxShadow: "0 12px 48px rgba(0,0,0,0.4)",
								overflow: "hidden",
								flexShrink: 0,
								animation: "er-modal-content-in 0.28s cubic-bezier(0.34, 1.2, 0.64, 1) both",
							}}
							onClick={(e) => e.stopPropagation()}
						>
							{hintDesignModal.design?.backgroundImageUrl ? (
								<EscapeRoomBackgroundMediaLayer
									mediaUrl={resolveEscapeRoomMediaUrl(hintDesignModal.design.backgroundImageUrl)}
									opacity={hintDesignModal.design.backgroundImageOpacity != null ? hintDesignModal.design.backgroundImageOpacity : 100}
									zIndex={0}
									borderRadius={hintDesignModal.design?.borderRadius ?? 12}
								/>
							) : null}
							<div style={{ position: "absolute", left: 0, top: 0, width: "100%", height: "100%", overflow: "hidden", zIndex: 1 }}>
								{(hintDesignModal.design?.elements || []).map((el) => (
									<EscapeRoomElement
										key={el.elementId}
										element={el}
										quizData={el.type === "quiz" ? quizzes[resolveQuizId(el.content?.quizId)] : undefined}
										answer={null}
										onButtonAction={(elem) => handleHintModalButtonAction(elem, hintDesignModal)}
										onQuizOptionSelect={() => {}}
										onHintUsed={() => {}}
										playerScore={group?.score ?? 0}
									/>
								))}
							</div>
							<EscapeRoomModalCloseButton
								onClick={() => setHintDesignModal(null)}
								title={t("COMMON.CLOSE")}
							/>
						</div>
					)}
				</div>
			)}
			{modalContent && (
				<div
					style={{
						position: "fixed",
						inset: 0,
						zIndex: 9999,
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						background: "rgba(0,0,0,0.6)",
						padding: 16,
						boxSizing: "border-box",
						animation: "er-modal-overlay-in 0.22s ease-out both",
					}}
					onClick={(e) => e.target === e.currentTarget && setModalContent(null)}
				>
					<div
						style={{
							position: "relative",
							width: modalContent.width,
							height: modalContent.height,
							minWidth: 200,
							minHeight: 150,
							...escapeRoomEditorBackgroundSurfaceStyle(modalContent.design?.backgroundColor || "#ffffff"),
							borderRadius: modalContent.design?.borderRadius ?? 12,
							boxShadow: "0 12px 48px rgba(0,0,0,0.4)",
							overflow: "hidden",
							flexShrink: 0,
							animation: "er-modal-content-in 0.28s cubic-bezier(0.34, 1.2, 0.64, 1) both",
						}}
						onClick={(e) => e.stopPropagation()}
					>
						{modalContent.design?.backgroundImageUrl ? (
							<EscapeRoomBackgroundMediaLayer
								mediaUrl={resolveEscapeRoomMediaUrl(modalContent.design.backgroundImageUrl)}
								opacity={modalContent.design.backgroundImageOpacity != null ? modalContent.design.backgroundImageOpacity : 100}
								zIndex={0}
								borderRadius={modalContent.design?.borderRadius ?? 12}
							/>
						) : null}
						<div style={{ position: "absolute", left: 0, top: 0, width: "100%", height: "100%", overflow: "hidden", zIndex: 1 }}>
							{(() => {
								const quizIds = (modalElements || []).filter((e) => e.type === "quiz").map((e) => e.elementId);
								const allQuizzesOnModalCompleted = quizIds.length === 0 || quizIds.every((id) => {
									const a = answers[id];
									return !!(a && a.done);
								});
								const activatedSet = activatedButtonsByPage[String(pageIndex)] || new Set();
								const modalButtons = (modalElements || []).filter((e) => e.type === "button");
								return (modalContent.design?.elements || []).map((el) => {
									let buttonVisible = true;
									let buttonDisabled = false;
									if (el.type === "button") {
										const showWhen = el.content?.showWhen || "always";
										if (showWhen === "quizCompleted") {
											buttonVisible = allQuizzesOnModalCompleted;
										} else if (showWhen === "allButtonsActivated") {
											const otherButtons = modalButtons.filter((b) => b.elementId !== el.elementId);
											buttonVisible = otherButtons.length === 0 || otherButtons.every((b) => activatedSet.has(b.elementId));
										} else if (showWhen === "afterSeconds") {
											buttonVisible = modalAfterSecondsReady.has(String(el.elementId));
										}
										if (buttonVisible && modalHideAfterExpired.has(String(el.elementId))) {
											buttonVisible = false;
										}
										buttonDisabled = !!el.content?.onlyOnce && activatedSet.has(el.elementId);
									}
									if (el.type === "button" && !buttonVisible) return null;
									return (
								<EscapeRoomElement
									key={el.elementId}
									element={el}
									quizData={el.type === "quiz" ? quizzes[resolveQuizId(el.content?.quizId)] : undefined}
									answer={answers[el.elementId]}
									onButtonAction={handleButtonAction}
									onQuizOptionSelect={handleQuizOptionSelect}
									onQuizCustomAnswer={handleQuizCustomAnswer}
									onHintUsed={handleHintUsed}
									allQuizzesOnPageCompleted={allQuizzesOnModalCompleted}
									buttonVisible={el.type === "button" ? buttonVisible : undefined}
									buttonDisabled={el.type === "button" ? buttonDisabled : undefined}
									playerScore={group?.score ?? 0}
								/>
									);
								});
							})()}
						</div>
						<EscapeRoomModalCloseButton
							onClick={() => setModalContent(null)}
							title={t("COMMON.CLOSE")}
						/>
					</div>
				</div>
			)}
		</div>
	);
}
